/* eslint-disable max-lines, max-lines-per-function */
import {
  createContext,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './RequestHistory.scss';
import { useAuth } from '../../../../Hooks/useAuth';
import useProjectDetailsData from '../useProjectDetailsData';
import ViewRequestModal from './ViewRequestModal';
import AddRequestModal from './AddRequestModal';
import EditRequestModal from './EditRequestModal';
import RequestStatusModal from './RequestStatusModal';
import { CancelRequestModal } from './requestHistoryModal';
import { primeProjectStatusOptions } from './statusOptionsShared';
import { STATUS_CHANGE_REQUESTS } from '../../../../Api/resources/endPoint';
import { serviceName } from '../../../../Api/resources/serviceConstants';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../../Api/resources/serviceURLs';
import UseAxios from '../../../../Api/http/http';
import {
  approveStatusChangeRequest,
  createStatusChangeRequest,
  deleteStatusChangeRequest,
  getStatusChangeRequestById,
  listStatusChangeRequestHistory,
  updateStatusChangeRequest,
} from '../../../../Api/request/statusChangeRequest';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import { MODULE_NAMES } from '../../../../Constants/modules';
import EditActionIcon from '../../../../Assets/images/svg/dar_edit.svg';
import DeleteActionIcon from '../../../../Assets/images/svg/dar_delete.svg';
import CopyActionIcon from '../../../../Assets/images/svg/copy.svg';

const REQUEST_STATUS_FILTER_FIELD = 'requestStatusValue';
const ASSIGNED_TO_ME_FILTER_FIELD = 'assignedToMeLabel';
const MAX_REQUESTED_DATE_RANGE_DAYS = 90;
const REQUEST_HISTORY_TABLE_STATE_VERSION = 'v12';
const ALL_PROJECTS_QUERY_KEY = 'Allprojects';
const REQUEST_HISTORY_ORIGIN_CONTEXT_KEY = 'request-history-origin-context';
const ASSIGNED_TO_ME_ICON_COLOR = 'var(--request-history-assigned-icon-color)';
const REQUEST_STATUS_LABELS = {
  0: 'Open',
  1: 'Approved',
  2: 'Rejected',
  3: 'Cancelled',
};
const OPEN_REQUEST_BLOCK_MESSAGE = 'A new request can not be raised as one request is already open.';
const STATUS_CLASS_MAP = {
  open: 'status-open',
  approved: 'status-approved',
  rejected: 'status-rejected',
  cancelled: 'status-canceled',
  canceled: 'status-canceled',
};
const PROJECT_STATUS_CLASS_MAP = [
  { match: ['closed', 'closure', 'completed'], className: 'closed' },
  { match: ['dropped', 'drop', 'cancelled', 'canceled'], className: 'dropped' },
  { match: ['hold', 'on hold', 'pending'], className: 'on-hold' },
  { match: ['in progress', 'progress', 'active', 'open'], className: 'in-progress' },
];

const getProjectSeqIdFromLocation = (search) => {
  const params = new URLSearchParams(search || '');
  return params.get('projectSeqId');
};

const getProjectIdFromLocation = (search) => {
  const params = new URLSearchParams(search || '');
  return params.get('projectId');
};

const getStatusChangeIdFromLocation = (search) => {
  const params = new URLSearchParams(search || '');
  return params.get('id') || params.get('statusChangeId');
};

const getModeFromLocation = (search) => {
  const params = new URLSearchParams(search || '');
  if (params.has(ALL_PROJECTS_QUERY_KEY)) {
    return 'all';
  }
  return params.get('mode');
};

const isFromNotificationFromLocation = (search) => {
  const params = new URLSearchParams(search || '');
  return params.get('fromNotification') === 'true';
};

const buildRequestHistoryUrl = ({
  projectId,
  statusChangeId,
  mode,
} = {}) => {
  if (mode) {
    if (mode === 'all') {
      const allProjectsParams = new URLSearchParams();
      allProjectsParams.append(ALL_PROJECTS_QUERY_KEY, '');
      const queryString = allProjectsParams.toString().replace(`${ALL_PROJECTS_QUERY_KEY}=`, ALL_PROJECTS_QUERY_KEY);
      return `/delivery/project-details/request-history?${queryString}`;
    }
    const modeParams = new URLSearchParams();
    modeParams.set('mode', mode);
    return `/delivery/project-details/request-history?${modeParams.toString()}`;
  }

  const params = new URLSearchParams();
  if (projectId) params.set('projectId', String(projectId));
  if (statusChangeId) params.set('statusChangeId', String(statusChangeId));
  const queryString = params.toString();
  return `/delivery/project-details/request-history${queryString ? `?${queryString}` : ''}`;
};

const buildStatusRequestShareUrl = (row = {}) => {
  const rowProjectId = row?.projectId ?? row?.project_id ?? null;
  const rowStatusChangeId = row?.statusChangeId ?? row?.status_change_id ?? null;

  return buildRequestHistoryUrl({
    projectId: rowProjectId,
    statusChangeId: rowStatusChangeId,
  });
};

const getRequestHistoryShareBaseUrl = () => {
  const configuredAppUrl = String(process.env.REACT_APP_API_URL || '')
    .trim()
    .replace(/\/$/, '');
  if (configuredAppUrl) {
    return configuredAppUrl;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin)
      .trim()
      .replace(/\/$/, '');
  }
  return '';
};

const toAbsoluteRequestShareUrl = (sharePath = '') => {
  if (!sharePath) return '';
  const shareBaseUrl = getRequestHistoryShareBaseUrl();
  if (!shareBaseUrl) return sharePath;
  try {
    return new URL(sharePath, shareBaseUrl).toString();
  } catch (error) {
    return sharePath;
  }
};

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${STATUS_CHANGE_REQUESTS}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${STATUS_CHANGE_REQUESTS}`;
};

const baseURL = fetchBaseURL();

const normalizeTone = (tone) => String(tone || '')
  .trim()
  .toLowerCase();

const getStatusClassName = (tone) => STATUS_CLASS_MAP[normalizeTone(tone)] || '';
const getProjectStatusClassName = (value) => {
  const normalized = normalizeTone(value);
  if (!normalized || normalized === '-') return '';
  const matchedTone = PROJECT_STATUS_CLASS_MAP.find(({ match = [] }) => (
    match.some((entry) => normalized.includes(entry))
  ));
  return matchedTone?.className || '';
};

const toIsoDate = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
    const [day, month, year] = text.split('/');
    return `${year}-${month}-${day}`;
  }
  return text;
};

const parseIsoDateValue = (value) => {
  const isoValue = toIsoDate(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoValue)) return null;

  const [year, month, day] = isoValue.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatIsoDateValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  return `${value.getUTCFullYear()}-${pad2(value.getUTCMonth() + 1)}-${pad2(value.getUTCDate())}`;
};

const getRequestedDateRangeCorrection = (range = []) => {
  if (!Array.isArray(range) || range.length < 2 || !range[0] || !range[1]) {
    return null;
  }

  const startDate = parseIsoDateValue(range[0]);
  const endDate = parseIsoDateValue(range[1]);
  if (!startDate || !endDate || endDate < startDate) {
    return null;
  }

  const maxAllowedEndDate = new Date(startDate);
  maxAllowedEndDate.setUTCDate(maxAllowedEndDate.getUTCDate() + (MAX_REQUESTED_DATE_RANGE_DAYS - 1));

  if (endDate <= maxAllowedEndDate) {
    return null;
  }

  return {
    correctedRange: [formatIsoDateValue(startDate), formatIsoDateValue(maxAllowedEndDate)],
    correctedEndDate: formatIsoDateValue(maxAllowedEndDate),
  };
};

const applyRequestedDateRangeCorrection = (tableConfiguration = [], correctedRange = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => ({
      ...column,
      menuItems: Array.isArray(column?.menuItems)
        ? column.menuItems.map((menuItem) => ({
          ...menuItem,
          items: Array.isArray(menuItem?.items)
            ? menuItem.items.map((item) => (
              item?.type === 'filter' && item?.field === 'requestedDate' && item?.template === 'dateRange'
                ? {
                  ...item,
                  range: correctedRange,
                  selectedValues: correctedRange.filter(Boolean),
                }
                : item
            ))
            : menuItem?.items,
        }))
        : column?.menuItems,
    }))
    : tableConfiguration
);

const safeReadSessionJson = (key) => {
  if (!key || typeof window === 'undefined') return null;

  try {
    const rawValue = window.sessionStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const safeWriteSessionJson = (key, value) => {
  if (!key || typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore session storage write failures.
  }
};

const safeRemoveSessionItem = (key) => {
  if (!key || typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // Ignore session storage delete failures.
  }
};

const pad2 = (value) => String(value).padStart(2, '0');

const getCurrentDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

const getDateDaysAgoString = (daysAgo = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - Number(daysAgo || 0));
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const getDefaultRequestedDateRange = () => [
  getDateDaysAgoString(MAX_REQUESTED_DATE_RANGE_DAYS - 1),
  getCurrentDateString(),
];

const getCurrentTimeString = () => {
  const now = new Date();
  return `${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;
};

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const getApiErrorMessage = (error, fallbackMessage) => {
  const axiosData = error?.response?.data;
  return axiosData?.message || error?.message || fallbackMessage;
};

const getResolvedEmployeeName = (profile = {}) => (
  profile?.name
  || profile?.userName
  || profile?.user_name
  || profile?.employeeName
  || profile?.employee_name
  || profile?.empName
  || profile?.emp_name
  || ''
);

const getResolvedEmployeeEmail = (profile = {}) => (
  profile?.email
  || profile?.email_id
  || profile?.emailId
  || profile?.login_id
  || profile?.loginId
  || ''
);

const getResolvedEmployeeNumber = (profile = {}) => (
  profile?.employeeNo
  || profile?.employee_no
  || profile?.empNo
  || profile?.emp_no
  || ''
);

const isSuperAdminUser = (userProfile) => {
  if (!userProfile || Array.isArray(userProfile)) return false;

  const superAdminFlag = userProfile?.superAdmin ?? userProfile?.super_admin;
  if (Number(superAdminFlag) === 1 || superAdminFlag === true) {
    return true;
  }

  const roleDetails = Array.isArray(userProfile?.roleDetails)
    ? userProfile.roleDetails
    : userProfile?.roleDetails
      ? [userProfile.roleDetails]
      : [];

  return roleDetails.some((role) => {
    const roleName = String(role?.roleName || role?.name || '')
      .trim()
      .toLowerCase();
    const roleId = Number(role?.role_id ?? role?.roleId);
    return roleName === 'super admin' || roleId === 1;
  });
};

const normalizePermissionAction = (action) => {
  const normalized = String(action || '').trim()
    .toLowerCase();
  if (normalized === 'view') return 'read';
  if (normalized === 'add') return 'create';
  if (normalized === 'edit') return 'update';
  return normalized;
};

const getRequestApprovalValue = (row) => Number(
  row?.statusDisplay?.value
  ?? row?.requestStatusValue
  ?? row?.isApproved,
);

const getRequestStatusForEdit = (row) => String(
  row?.statusDisplay?.label
  ?? row?.requestStatusInfo?.requestStatus
  ?? row?.requestStatus
  ?? row?.request_status
  ?? '',
)
  .trim()
  .toLowerCase();

const isEditDisabledByStatus = (row) => {
  const statusLabel = getRequestStatusForEdit(row);
  if (['approved', 'closed', 'rejected', 'cancelled', 'canceled'].includes(statusLabel)) {
    return true;
  }

  const approvalValue = getRequestApprovalValue(row);
  return [1, 2, 3].includes(approvalValue);
};

const isRequestActionable = (row) => {
  const statusLabel = getRequestStatusForEdit(row);
  if (['approved', 'closed', 'rejected', 'cancelled', 'canceled'].includes(statusLabel)) {
    return false;
  }

  if (statusLabel === 'open') {
    return true;
  }

  return Boolean(
    row?.statusDisplay?.isActionable
    ?? (getRequestApprovalValue(row) === 0),
  );
};
const isRequestEditable = (row) => isRequestActionable(row);
const isRequestCancelable = (row) => isRequestActionable(row);

const RequestHistorySelectionContext = createContext({
  rowIds: [],
  selectedRowIds: [],
  canUpdateStatus: false,
  onToggleRowSelect: () => { },
  onToggleSelectAll: () => { },
});
const getRowSelectionId = (row = {}) => String(
  row?.statusChangeId
  ?? row?.status_change_id
  ?? row?.scrn
  ?? row?.srNumber
  ?? '',
);

const SelectionHeaderCell = () => {
  return null;
};

const SelectionRowCell = ({ row }) => {
  const {
    selectedRowIds = [],
    canUpdateStatus = false,
    onToggleRowSelect,
  } = useContext(RequestHistorySelectionContext);
  const rowId = getRowSelectionId(row);
  const checked = rowId ? selectedRowIds.includes(rowId) : false;
  const selectable = isRequestActionable(row);

  return (
    <div className="request-history-select-cell">
      <input
        type="checkbox"
        checked={checked}
        disabled={!canUpdateStatus || !selectable}
        onChange={(event) => {
          event.stopPropagation();
          onToggleRowSelect?.(rowId, event.target.checked);
        }}
        onClick={(event) => event.stopPropagation()}
      />
    </div>
  );
};
SelectionRowCell.displayName = 'SelectionRowCell';
SelectionRowCell.propTypes = {
  row: PropTypes.object,
};

const ScrnRowCell = ({ row, onScrnClick }) => {
  const scrnValue = row?.scrn || row?.srNumber || '-';

  return (
    <div className="request-history-scrn-cell">
      <button
        type="button"
        className="request-history-scrn-link"
        onClick={(event) => {
          event.stopPropagation();
          onScrnClick?.(row);
        }}
      >
        {scrnValue}
      </button>
    </div>
  );
};
ScrnRowCell.displayName = 'ScrnRowCell';
ScrnRowCell.propTypes = {
  row: PropTypes.object,
  onScrnClick: PropTypes.func,
};

const renderTruncatedTextCell = (value, tooltipValue = value) => {
  const label = String(value || '').trim() || '-';
  const title = String(tooltipValue || label).trim() || label;

  if (label === '-') {
    return <span className="request-history-truncate-text">{label}</span>;
  }

  return (
    <Tooltip title={title} arrow placement="top">
      <span className="request-history-truncate-text">{label}</span>
    </Tooltip>
  );
};

const renderPersonCell = (
  person = {},
  tooltipPrefix = 'Approver',
  tooltipPerson = person,
) => {
  const name = String(person?.name || '').trim() || '-';
  const empId = String(person?.empId || '').trim();
  const tooltipName = String(tooltipPerson?.name || '').trim() || '-';
  const tooltipEmpId = String(tooltipPerson?.empId || '').trim();
  const tooltipEmail = String(tooltipPerson?.email || '').trim();
  const hasDetails = tooltipName !== '-' || tooltipEmpId || tooltipEmail;
  const display = empId ? `${empId} - ${name}` : name;
  const tooltipContent = (
    <>
      <div>{tooltipPrefix} Emp name: {tooltipName || '-'}</div>
      <div>{tooltipPrefix} Emp id: {tooltipEmpId || '-'}</div>
      <div>{tooltipPrefix} Emp email id: {tooltipEmail || '-'}</div>
    </>
  );

  const content = (
    <div className="request-person-cell">
      <span className="request-person-name">{display}</span>
    </div>
  );

  if (!hasDetails || display === '-') {
    return content;
  }

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      {content}
    </Tooltip>
  );
};

const renderRequestStatusCell = (row, context = {}) => {
  const status = row?.statusDisplay || {};
  const toneClass = getStatusClassName(status?.tone);
  const canAct = Boolean(status?.isActionable && typeof context.onStatusClick === 'function');
  const canViewStatus = Boolean(context.canViewStatus);
  const canUpdateStatus = Boolean(context.canUpdateStatus);
  const statusLabel = status?.label || row?.requestStatus || '-';
  const isOpenStatus = String(statusLabel)
    .trim()
    .toLowerCase() === 'open';
  const isClickable = Boolean(canAct && canViewStatus && canUpdateStatus && isOpenStatus);

  if (isClickable) {
    return (
      <button
        type="button"
        className={`status-badge ${toneClass}`.trim()}
        onClick={(event) => {
          event.stopPropagation();
          context.onStatusClick(row);
        }}
      >
        {statusLabel}
      </button>
    );
  }

  return (
    <div className="status-text">
      <div className={`status-text__label ${toneClass}`.trim()}>
        {statusLabel}
      </div>
    </div>
  );
};

const renderProjectStatusCell = (value) => {
  const label = String(value || '').trim() || '-';
  const toneClass = getProjectStatusClassName(label);

  return (
    <div className="status-text">
      <div className={`status-text__label ${toneClass}`.trim()}>
        {label}
      </div>
    </div>
  );
};

const isResolvedRequestStatus = (row = {}) => [1, 2, 3].includes(getRequestApprovalValue(row));

const getApprovedByPerson = (row = {}) => {
  if (!isResolvedRequestStatus(row)) {
    return null;
  }

  const name = row?.approvedByName || row?.approved_by_name || '';
  const empId = row?.approvedByEmpId
    || row?.approvedEmpId
    || row?.approved_id
    || row?.approvedBy
    || row?.approved_by
    || '';
  const email = row?.approvedByEmail || row?.approved_email || '';

  if (!name && !empId && !email) {
    return null;
  }

  return { name, empId, email };
};

const buildApprovedByFilterOptions = (rows = []) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  const seenValues = new Set();

  return rows.reduce((acc, row) => {
    const value = row?.approvedBy ?? row?.approved_by ?? '';
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue || normalizedValue === '0' || seenValues.has(normalizedValue)) {
      return acc;
    }

    const person = getApprovedByPerson(row);
    const label = String(person?.name || person?.empId || normalizedValue).trim();
    seenValues.add(normalizedValue);
    acc.push({
      value: normalizedValue,
      label,
      name: person?.name || label,
      empId: person?.empId || '',
      email: person?.email || '',
    });
    return acc;
  }, []);
};

const renderApprovedByCell = (row = {}) => {
  const person = getApprovedByPerson(row);
  if (!person) {
    return renderTruncatedTextCell('-');
  }

  return renderPersonCell(person, 'Approved by', person);
};

const getApprovedDateTimeParts = (row = {}) => {
  if (!isResolvedRequestStatus(row)) {
    return [];
  }

  const approvedDate = String(row?.approvedDate || '').trim();
  const approvedTime = String(row?.approvedTime || '').trim();
  const normalizedDate = approvedDate && approvedDate !== '9999-01-01' ? approvedDate : '';
  const normalizedTime = approvedTime && approvedTime !== '00:00:00' ? approvedTime : '';

  return [normalizedDate, normalizedTime].filter(Boolean);
};

const renderApprovedDateTimeCell = (row = {}) => {
  const details = getApprovedDateTimeParts(row);

  if (!details.length) {
    return renderTruncatedTextCell('-');
  }

  return (
    <div className="status-text">
      {details.map((detail, index) => (
        <div
          key={`${row?.statusChangeId || row?.srNumber || 'approved-date-time'}-${index}`}
          className="status-text__detail"
        >
          {detail}
        </div>
      ))}
    </div>
  );
};

const renderActionCell = (row, context = {}) => {
  const canUpdate = Boolean(context.canUpdate);
  const canDelete = Boolean(context.canDelete);
  const canShare = Boolean(context.canShareLink && context.onShareClick && row?.requestShareUrl);

  if (!canUpdate && !canDelete && !canShare) {
    return null;
  }

  const editStatus = getRequestStatusForEdit(row);
  const hasResolvedStatus = Boolean(editStatus);
  const isOpenStatus = editStatus === 'open';
  const canRenderEdit = canUpdate;
  const canRenderDelete = canDelete;
  const canRenderShare = canShare;
  const actionCount = Number(canRenderEdit) + Number(canRenderDelete) + Number(canRenderShare);
  const editDisabled = (hasResolvedStatus ? !isOpenStatus : isEditDisabledByStatus(row))
    || !isRequestEditable(row);
  const cancelDisabled = (hasResolvedStatus ? !isOpenStatus : !isRequestCancelable(row))
    || !isRequestCancelable(row);
  return (
    <div className={`request-history-actions-cell${actionCount === 1 ? ' request-history-actions-cell--single' : ''}`}>
      {canRenderEdit ? (
        <span
          className={`request-history-action-shell${editDisabled ? ' is-disabled' : ''}`}
          title={editDisabled ? '' : 'Edit'}
        >
          <button
            type="button"
            className={`request-history-action-btn${editDisabled ? ' is-disabled' : ''}`}
            disabled={editDisabled}
            aria-label="Edit request"
            onClick={(event) => {
              event.stopPropagation();
              if (!editDisabled) {
                context.onEditClick?.(row);
              }
            }}
          >
            <span className="request-history-action-btn-icon" aria-hidden="true">
              <img src={EditActionIcon} alt="" />
            </span>
          </button>
        </span>
      ) : null}
      {canRenderDelete ? (
        <span
          className={`request-history-action-shell${cancelDisabled ? ' is-disabled' : ''}`}
          title={cancelDisabled ? '' : 'Cancel request'}
        >
          <button
            type="button"
            className={
              `request-history-action-btn request-history-action-btn--delete${cancelDisabled ? ' is-disabled' : ''}`
            }
            disabled={cancelDisabled}
            aria-label="Cancel request"
            onClick={(event) => {
              event.stopPropagation();
              if (!cancelDisabled) {
                context.onDeleteClick?.(row);
              }
            }}
          >
            <span className="request-history-action-btn-icon" aria-hidden="true">
              <img src={DeleteActionIcon} alt="" />
            </span>
          </button>
        </span>
      ) : null}
      {canRenderShare ? (
        <span
          className="request-history-action-shell"
          title="Copy share link"
        >
          <button
            type="button"
            className="request-history-action-btn"
            aria-label="Copy share link"
            onClick={(event) => {
              event.stopPropagation();
              context.onShareClick?.(row);
            }}
          >
            <span className="request-history-action-btn-icon" aria-hidden="true">
              <img src={CopyActionIcon} alt="" />
            </span>
          </button>
        </span>
      ) : null}
    </div>
  );
};

const mapRowForModal = (row) => ({
  scrn: row?.scrn || row?.srNumber || '-',
  requestedBy: { name: row?.requestedByName || row?.requesterName || '-' },
  requestedDate: row?.requestedDateTime || `${row?.requestedDate || ''} ${row?.requestedTime || ''}`.trim(),
  project: { name: row?.projectName || '-' },
  oldStatus: row?.oldStatus || row?.old_status || '-',
  newStatus: row?.newStatus || row?.new_status || '-',
  effectiveDate: row?.effectiveDate || row?.effective_date || '-',
  remarks: row?.remarks || row?.reason || '-',
  reason: row?.remarks || row?.reason || '-',
  approver: { name: row?.approverName || '-' },
  requestStatus: row?.requestStatus || row?.statusDisplay?.label || row?.status_display?.label || '-',
  approveRemarks: row?.statusDisplay?.approvedRemarks || row?.approvedRemarks || row?.approved_remarks || '-',
});

const mergeTemplateEntry = (existing = [], nextEntry) => {
  if (!nextEntry?.field) return existing;
  const index = existing.findIndex((entry) => entry.field === nextEntry.field);
  if (index === -1) return [...existing, nextEntry];
  const updated = [...existing];
  updated[index] = { ...updated[index], ...nextEntry };
  return updated;
};

const applyFormatStylesToRows = (columns = [], rows = []) => {
  if (!Array.isArray(columns) || !Array.isArray(rows) || !rows.length) return rows;

  const styleEntries = columns.flatMap((column) => {
    const template = column?.format?.template;
    if (!Array.isArray(template)) return [];
    return template
      .filter((entry) => Array.isArray(entry?.style) && entry?.field)
      .map((entry) => ({
        field: entry.field,
        styles: entry.style,
      }));
  });

  if (!styleEntries.length) return rows;

  return rows.map((row) => {
    let template = Array.isArray(row?.rowConfiguration?.template)
      ? [...row.rowConfiguration.template]
      : [];

    styleEntries.forEach(({ field, styles }) => {
      const rawValue = row?.[field];
      if (rawValue === undefined || rawValue === null) return;
      const normalizedValue = String(rawValue)
        .trim()
        .toLowerCase();
      const match = styles.find(
        (style) => String(style?.value ?? '')
          .trim()
          .toLowerCase() === normalizedValue,
      );
      if (!match) return;
      template = mergeTemplateEntry(template, {
        field,
        textColor: match.textColor,
        boxColor: match.boxColor,
        fontWeight: match.fontWeight || '600',
      });
    });

    if (!template.length) return row;

    return {
      ...row,
      rowConfiguration: {
        ...(row.rowConfiguration || {}),
        template,
      },
    };
  });
};

const withFilterRequestScope = (url, scope = {}) => {
  if (!url) return url;
  const searchParams = new URLSearchParams();
  const cacheScope = String(scope?.cacheScope || '').trim();
  const loggedInEmpId = String(scope?.loggedInEmpId || '').trim();
  const projectId = String(scope?.projectId || '').trim();
  const projectSeqId = String(scope?.projectSeqId || '').trim();
  const projectNameValues = normalizeFilterValues(scope?.projectName)
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  if (cacheScope) searchParams.set('cache_scope', cacheScope);
  if (loggedInEmpId) searchParams.set('logged_in_emp_id', loggedInEmpId);
  if (projectId) searchParams.set('project_id', projectId);
  if (projectSeqId) searchParams.set('project_seq_id', projectSeqId);
  if (projectNameValues.length) searchParams.set('project_name', projectNameValues.join(','));

  const scopedQuery = searchParams.toString();
  if (!scopedQuery) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${scopedQuery}`;
};

const decorateFilterItem = (item = {}, context = {}) => {
  if (item?.type !== 'filter') return item;
  const scopedUrl = withFilterRequestScope(item?.url, context.filterRequestScope);

  if (item?.field === 'assignedToMe' && (item?.template === 'choice' || item?.template === 'multiSelect')) {
    const normalizedValues = normalizeFilterValues(item?.selectedValues);
    const isAssignedToMeSelected = normalizedValues.some((value) => (
      value === true
      || value === 'true'
      || value === 1
      || value === '1'
      || value === 'Assigned to me'
    ));
    const seededAssignedToMeValues = normalizeFilterValues(context.assignedToMeSeedValues);
    const hasSeededAssignedToMe = seededAssignedToMeValues.some((value) => (
      value === true
      || value === 'true'
      || value === 1
      || value === '1'
      || value === 'Assigned to me'
    ));
    const assignedToMeOption = {
      label: 'Assigned to me',
      value: 'Assigned to me',
      iconColor: ASSIGNED_TO_ME_ICON_COLOR,
    };
    const selectedAssignedToMeValues = context.forceDefaultAssignedToMe
      ? (hasSeededAssignedToMe ? ['Assigned to me'] : [])
      : (
        context.assignedToMeExplicitlyCleared
          ? []
          : (isAssignedToMeSelected || hasSeededAssignedToMe ? ['Assigned to me'] : [])
      );
    return {
      ...item,
      template: 'multiSelect',
      field: ASSIGNED_TO_ME_FILTER_FIELD,
      filterCloneKey: getReqFilterCloneKey(ASSIGNED_TO_ME_FILTER_FIELD),
      tableUtilityConfig: {
        ...(item?.tableUtilityConfig || {}),
        filterCloneKey: getReqFilterCloneKey(ASSIGNED_TO_ME_FILTER_FIELD),
      },
      label: 'Approver',
      placeholder: 'Select',
      url: undefined,
      values: [assignedToMeOption],
      options: [assignedToMeOption],
      selectedValues: selectedAssignedToMeValues,
    };
  }

  if (item?.field === 'projectName' && item?.template === 'multiSelect') {
    const normalizedValues = normalizeFilterValues(item?.selectedValues);
    const seededProjectValues = normalizeFilterValues(context.projectFilterSeedValues);
    const selectedProjectValues = context.forceDefaultProjectName
      ? seededProjectValues
      : (normalizedValues.length ? normalizedValues : seededProjectValues);
    return {
      ...item,
      label: 'Project Name',
      placeholder: 'Select',
      url: scopedUrl,
      selectedValues: selectedProjectValues,
    };
  }

  if ((item?.field === 'requestStatus' || item?.field === REQUEST_STATUS_FILTER_FIELD)
    && item?.template === 'multiSelect') {
    const normalizedValues = normalizeFilterValues(item?.selectedValues);
    const seededRequestStatusValues = normalizeFilterValues(context.requestStatusSeedValues);
    const selectedRequestStatusValues = context.forceDefaultRequestStatus
      ? seededRequestStatusValues
      : (normalizedValues.length ? normalizedValues : seededRequestStatusValues);
    return {
      ...item,
      field: REQUEST_STATUS_FILTER_FIELD,
      label: 'Request Status',
      placeholder: 'Select',
      url: scopedUrl,
      options: Object.entries(REQUEST_STATUS_LABELS).map(([value, label]) => ({
        value: Number(value),
        label,
      })),
      selectedValues: selectedRequestStatusValues,
    };
  }

  if (item?.field === 'projectVertical' && item?.template === 'multiSelect') {
    return { ...item, label: 'Project Vertical', placeholder: 'Select', url: scopedUrl };
  }

  if (item?.field === 'projectType' && item?.template === 'multiSelect') {
    return { ...item, label: 'Project Type', placeholder: 'Select', url: scopedUrl };
  }

  if (item?.field === 'approvedBy' && item?.template === 'multiSelect') {
    const normalizedValues = normalizeFilterValues(item?.selectedValues)
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    const approvedByOptions = Array.isArray(context.approvedByOptions)
      ? context.approvedByOptions
      : [];
    return {
      ...item,
      label: 'Approved By',
      filterCloneKey: getReqFilterCloneKey('approvedBy'),
      tableUtilityConfig: {
        ...(item?.tableUtilityConfig || {}),
        filterCloneKey: getReqFilterCloneKey('approvedBy'),
      },
      placeholder: 'Select',
      url: scopedUrl,
      values: approvedByOptions,
      options: approvedByOptions,
      selectedValues: normalizedValues,
    };
  }

  if (item?.field === 'requestedBy' && item?.template === 'multiSelect') {
    return { ...item, label: 'Requested By', placeholder: 'Select', url: scopedUrl };
  }

  if (item?.field === 'requestedDate' && item?.template === 'dateRange') {
    const currentRange = Array.isArray(item?.range) ? item.range : [null, null];
    const seededRange = Array.isArray(context.requestedDateSeedRange)
      ? context.requestedDateSeedRange
      : [];
    const normalizedRange = context.forceDefaultRequestedDate
      ? seededRange
      : (currentRange.some(Boolean) ? currentRange : seededRange);
    return {
      ...item,
      label: `Requested Date (Max ${MAX_REQUESTED_DATE_RANGE_DAYS} days)`,
      range: normalizedRange,
      selectedValues: normalizedRange.filter(Boolean),
      maxRangeDays: MAX_REQUESTED_DATE_RANGE_DAYS,
      maxRangeValidationMessage: `Please select a Requested Date range within ${MAX_REQUESTED_DATE_RANGE_DAYS} days.`,
    };
  }

  return item;
};

const remapRequestStatusFilterField = (item = {}) => {
  if (item?.type !== 'filter' || item?.template !== 'multiSelect' || item?.field !== 'requestStatus') {
    return item;
  }

  return {
    ...item,
    field: REQUEST_STATUS_FILTER_FIELD,
  };
};

const remapStatusFilters = (columns = [], context = {}) => (
  Array.isArray(columns)
    ? columns.map((column) => ({
      ...column,
      menuItems: Array.isArray(column?.menuItems)
        ? column.menuItems.map((menuItem) => ({
          ...menuItem,
          items: Array.isArray(menuItem?.items)
            ? menuItem.items.map((item) => decorateFilterItem(remapRequestStatusFilterField(item), context))
            : menuItem?.items,
        }))
        : column?.menuItems,
    }))
    : []
);

const normalizeFilterValues = (values = []) => {
  const sourceValues = Array.isArray(values)
    ? values
    : (values === undefined || values === null || values === '' ? [] : [values]);

  return sourceValues
    .map((value) => {
      if (value === undefined || value === null || value === '') return null;
      if (typeof value === 'object' && !Array.isArray(value)) {
        return value.value
          ?? value.id
          ?? value.code
          ?? value.key
          ?? value.name
          ?? value.label
          ?? null;
      }
      return value;
    })
    .filter((value) => value !== null && value !== undefined && value !== '');
};

const getReqFilterCloneKey = (field = '') => {
  if (field === 'approvedBy' || field === 'approvedByDisplay') {
    return 'request-history-approved-by';
  }

  if (field === 'assignedToMe' || field === ASSIGNED_TO_ME_FILTER_FIELD) {
    return 'request-history-assigned-to-me';
  }

  return '';
};

const getFilterItem = (tableConfiguration = [], fields = []) => {
  const fieldSet = new Set(Array.isArray(fields) ? fields : [fields]);

  for (const column of Array.isArray(tableConfiguration) ? tableConfiguration : []) {
    for (const menuItem of Array.isArray(column?.menuItems) ? column.menuItems : []) {
      for (const item of Array.isArray(menuItem?.items) ? menuItem.items : []) {
        if (item?.type === 'filter' && fieldSet.has(item?.field)) {
          return item;
        }
      }
    }
  }

  return null;
};

const createColumn = (base = {}, overrides = {}) => ({
  ...base,
  filterVisible: false,
  showFilterButtonInHeader: false,
  ...overrides,
});

const stripHeaderSortControls = (menuItems = []) => (
  (Array.isArray(menuItems) ? menuItems : []).map((menuItem) => {
    const nextItems = [];

    for (const item of Array.isArray(menuItem?.items) ? menuItem.items : []) {
      if (item?.type === 'sort') {
        continue;
      }

      if (item?.separator === true) {
        const previousItem = nextItems[nextItems.length - 1];
        if (!previousItem || previousItem?.separator === true) {
          continue;
        }
      }

      nextItems.push(item);
    }

    while (nextItems[nextItems.length - 1]?.separator === true) {
      nextItems.pop();
    }

    return {
      ...menuItem,
      items: nextItems.map((item) => (
        item?.type === 'filter'
          ? {
            ...item,
            showSortButtonInHeader: false,
          }
          : item
      )),
    };
  })
);

const relabelGlobalMenuItems = (menuItems = [], label = '') => (
  (Array.isArray(menuItems) ? menuItems : []).map((menuItem) => ({
    ...menuItem,
    label: label || menuItem?.label,
    isGlobalFilter: true,
    items: Array.isArray(menuItem?.items)
      ? menuItem.items.map((item) => ({
        ...item,
        label: label || item?.label,
        isGlobalFilter: true,
      }))
      : menuItem?.items,
  }))
);

const normalizeReqFilterItem = (item = {}, menuItemLabel = '') => {
  if (item?.type !== 'filter') {
    return item;
  }

  let labelName = '';
  if (menuItemLabel) {
    labelName = menuItemLabel;
  } else if (item.label) {
    labelName = item.label;
  } else if (item.field) {
    labelName = item.field.split('.').slice(-1)[0] || item.field;
  } else {
    labelName = 'Unknown';
  }

  const normalizedItem = {
    ...item,
    label: item?.label || (item?.field ? `Filter by ${labelName}` : ''),
  };
  const filterCloneKey = getReqFilterCloneKey(item?.field);
  if (filterCloneKey) {
    normalizedItem.filterCloneKey = filterCloneKey;
    normalizedItem.tableUtilityConfig = {
      ...(normalizedItem.tableUtilityConfig || {}),
      filterCloneKey,
    };
  }

  switch (item?.template) {
  case 'multiSelect':
  case 'choice':
    normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
      ? item.selectedValues
      : [];
    break;
  case 'singleSelect':
    normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
      ? item.selectedValues
      : [];
    normalizedItem.selectedValue = item?.selectedValue ?? null;
    normalizedItem.selectedLabel = item?.selectedLabel ?? '';
    break;
  case 'dateRange':
  case 'range':
    normalizedItem.range = Array.isArray(item?.range) && item.range.length >= 2
      ? [item.range[0] ?? null, item.range[1] ?? null]
      : [null, null];
    normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
      ? item.selectedValues
      : [];
    break;
  case 'inputfield':
    normalizedItem.inputValue = item?.inputValue ?? '';
    break;
  case 'valueFilter':
  case 'numberFilter':
    normalizedItem.condition = item?.condition ?? null;
    normalizedItem.value = item?.value ?? null;
    normalizedItem.range = Array.isArray(item?.range) && item.range.length >= 2
      ? [item.range[0] ?? null, item.range[1] ?? null]
      : [null, null];
    break;
  default:
    break;
  }

  return normalizedItem;
};

const normalizeReqTableResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const rawTableConfiguration = response.data?.tableConfiguration
    ?? response.data?.data?.tableConfiguration
    ?? response.tableConfiguration
    ?? response.tableConfig
    ?? response.data?.tableConfig
    ?? response.data?.data?.tableConfig
    ?? [];

  const rawTableData = response.data?.tableData
    ?? response.data?.data?.tableData
    ?? response.tableData
    ?? response.data?.data
    ?? [];

  const normalizeMenuItems = (menuItems = []) => (
    (Array.isArray(menuItems) ? menuItems : []).map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => (
          normalizeReqFilterItem(item, menuItem.label)
        ))
        : menuItem?.items,
    }))
  );

  const normalizedColumns = Array.isArray(rawTableConfiguration)
    ? rawTableConfiguration.map((column) => {
      const isFilterable = typeof column?.filter === 'boolean' ? column.filter : true;

      return {
        ...column,
        ...(column?.actions ? { actions: column.actions } : {}),
        filter: isFilterable,
        sortable: typeof column?.sortable === 'boolean' ? column.sortable : true,
        pinnable: typeof column?.pinnable === 'boolean' ? column.pinnable : true,
        filterVisible: false,
        showFilterButtonInHeader: false,
        showPinButtonInHeader:
          typeof column?.showPinButtonInHeader === 'boolean'
            ? column.showPinButtonInHeader
            : Boolean(column?.defaultPinned),
        menuItems: normalizeMenuItems(column?.menuItems),
      };
    })
    : [];

  const helperFieldSet = new Set(
    normalizedColumns
      .map((column) => column?.field)
      .filter(Boolean),
  );

  const helperColumns = normalizedColumns.flatMap((column, columnIndex) => (
    (Array.isArray(column?.menuItems) ? column.menuItems : []).flatMap((menuItem) => (
      (Array.isArray(menuItem?.items) ? menuItem.items : []).flatMap((item) => {
        if (item?.type !== 'filter' || !item?.field || item.field === column?.field) {
          return [];
        }

        if (helperFieldSet.has(item.field)) {
          return [];
        }

        helperFieldSet.add(item.field);

        const helperHeader = item?.label || menuItem?.label || column?.header || item.field;

        return [{
          field: item.field,
          header: helperHeader,
          filter: false,
          visible: false,
          defaultPinned: false,
          pinnable: false,
          sortable: false,
          filterVisible: false,
          showFilterButtonInHeader: false,
          showPinButtonInHeader: false,
          order: (normalizedColumns.length + 1) + columnIndex,
          menuItems: [{
            label: helperHeader,
            items: [normalizeReqFilterItem(item, menuItem.label)],
          }],
        }];
      })
    ))
  ));

  const tableConfiguration = [...normalizedColumns, ...helperColumns];
  const pagination = response.meta
    ?? response.pagination
    ?? response.paginationConfig
    ?? response.data?.meta
    ?? response.data?.pagination
    ?? response.data?.paginationConfig
    ?? response.data?.data?.meta
    ?? response.data?.data?.pagination
    ?? response.data?.data?.paginationConfig
    ?? null;

  response.data = {
    ...(response.data || {}),
    tableConfiguration,
    tableData: rawTableData,
  };

  response.meta = {
    ...(response.meta || {}),
    page: pagination?.page ?? response.meta?.page ?? 1,
    limit: pagination?.limit ?? response.meta?.limit ?? 10,
    total:
      pagination?.total
      ?? pagination?.count
      ?? response.meta?.total
      ?? (Array.isArray(rawTableData) ? rawTableData.length : 0),
  };

  response.data.meta = {
    ...(response.data?.meta || {}),
    ...response.meta,
  };

  if (typeof response.success === 'undefined') {
    response.success = Array.isArray(tableConfiguration) && Array.isArray(rawTableData);
  }

  return response;
};

const enhanceRequestHistoryColumns = (columns = [], context = {}) => {
  const sourceColumns = Array.isArray(columns) ? [...columns] : [];
  const getColumnByField = (field) => sourceColumns.find((column) => column?.field === field) || {};
  const canShowActionColumn = Boolean(context.canUpdate || context.canDelete);
  const hasSingleAction = Boolean(canShowActionColumn && !(context.canUpdate && context.canDelete));
  const actionColumnWidth = hasSingleAction ? 72 : 104;
  const actionColumnClassName = [
    'request-history-action-column',
    hasSingleAction ? 'request-history-action-column--single' : '',
  ].filter(Boolean).join(' ');

  const srnColumn = getColumnByField('srNumber');
  const requestedByDateColumn = getColumnByField('requestedByDate');
  const projectInfoColumn = getColumnByField('projectInfo');
  const oldStatusColumn = getColumnByField('oldStatus');
  const newStatusColumn = getColumnByField('newStatus');
  const effectiveDateColumn = getColumnByField('effectiveDate');
  const remarksColumn = getColumnByField('remarks');
  const approverInfoColumn = getColumnByField('approverInfo');
  const approvedByFilterColumn = getColumnByField('approvedBy');
  const requestStatusColumn = getColumnByField('requestStatusInfo');
  const approveRemarksColumn = getColumnByField('approvedRemarks');
  const hiddenColumns = sourceColumns.filter((column) => (
    ![
      'srNumber',
      'requestedByDate',
      'projectInfo',
      'approvedBy',
      'oldStatus',
      'newStatus',
      'effectiveDate',
      'remarks',
      'approverInfo',
      'requestStatusInfo',
      'approvedRemarks',
      'action',
    ].includes(column?.field)
  ));

  const requestedByMenuItems = Array.isArray(requestedByDateColumn?.menuItems)
    ? stripHeaderSortControls(
      requestedByDateColumn.menuItems.filter((menuItem) => menuItem?.label === 'Requested By'),
    )
    : [];
  const requestedDateMenuItems = Array.isArray(requestedByDateColumn?.menuItems)
    ? stripHeaderSortControls(
      requestedByDateColumn.menuItems.filter((menuItem) => menuItem?.label === 'Requested Date'),
    )
    : [];
  const projectNameMenuItems = Array.isArray(projectInfoColumn?.menuItems)
    ? stripHeaderSortControls(
      projectInfoColumn.menuItems.filter((menuItem) => menuItem?.label === 'Project Name'),
    )
    : [];
  const projectVerticalMenuItems = Array.isArray(projectInfoColumn?.menuItems)
    ? stripHeaderSortControls(
      projectInfoColumn.menuItems.filter((menuItem) => menuItem?.label === 'Project Vertical'),
    )
    : [];
  const approvedByMenuItems = Array.isArray(approvedByFilterColumn?.menuItems)
    ? relabelGlobalMenuItems(
      stripHeaderSortControls(
        approvedByFilterColumn.menuItems,
      ),
      'Approved By',
    )
    : [];
  const approvedDateMenuItems = Array.isArray(approverInfoColumn?.menuItems)
    ? relabelGlobalMenuItems(
      stripHeaderSortControls(
        approverInfoColumn.menuItems.filter((menuItem) => menuItem?.label === 'Approved Date'),
      ),
      'Approved Date / Time',
    )
    : [];

  const visibleColumns = [
    {
      field: 'selection',
      header: <SelectionHeaderCell />,
      order: 1,
      width: 48,
      visible: true,
      defaultPinned: true,
      pinnable: false,
      sortable: false,
      filter: false,
      filterVisible: false,
      showFilterButtonInHeader: false,
      showSortButtonInHeader: false,
      showPinButtonInHeader: false,
      isGlobalFilter: false,
      menuItems: [],
    },
    createColumn(srnColumn, {
      field: 'srNumber',
      header: 'SCRN',
      order: 2,
      width: 110,
      minWidth: 110,
      defaultPinned: true,
      pinnable: false,
      sortable: false,
      showSortButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: stripHeaderSortControls(srnColumn?.menuItems),
    }),
    createColumn(requestedByDateColumn, {
      field: 'requestedByDisplay',
      header: 'Requested By',
      order: 3,
      width: 220,
      minWidth: 220,
      format: undefined,
      filter: true,
      sortable: false,
      showSortButtonInHeader: false,
      showFilterButtonInHeader: false,
      menuItems: requestedByMenuItems,
    }),
    createColumn(requestedByDateColumn, {
      field: 'requestedDateDisplay',
      header: 'Requested Date',
      order: 4,
      width: 180,
      minWidth: 180,
      format: undefined,
      filter: true,
      sortable: false,
      showSortButtonInHeader: false,
      showFilterButtonInHeader: false,
      menuItems: requestedDateMenuItems,
    }),
    createColumn(projectInfoColumn, {
      field: 'projectNameDisplay',
      header: 'Project Name',
      order: 5,
      width: 240,
      minWidth: 240,
      format: undefined,
      filter: true,
      sortable: false,
      showSortButtonInHeader: false,
      showFilterButtonInHeader: false,
      menuItems: projectNameMenuItems,
    }),
    createColumn(projectInfoColumn, {
      field: 'projectVerticalDisplay',
      header: 'Project Vertical',
      order: 6,
      width: 190,
      minWidth: 190,
      format: undefined,
      filter: true,
      sortable: false,
      showSortButtonInHeader: false,
      showFilterButtonInHeader: false,
      menuItems: projectVerticalMenuItems,
    }),
    createColumn(oldStatusColumn, {
      field: 'oldStatusDisplay',
      header: 'Old Status',
      order: 7,
      width: 120,
      minWidth: 120,
      format: undefined,
      sortable: false,
      showSortButtonInHeader: false,
      menuItems: stripHeaderSortControls(oldStatusColumn?.menuItems),
    }),
    createColumn(newStatusColumn, {
      field: 'newStatusDisplay',
      header: 'New Status',
      order: 8,
      width: 120,
      minWidth: 120,
      format: undefined,
      sortable: false,
      showSortButtonInHeader: false,
      menuItems: stripHeaderSortControls(newStatusColumn?.menuItems),
    }),
    createColumn(effectiveDateColumn, {
      field: 'effectiveDate',
      header: 'Effective Date',
      order: 9,
      width: 155,
      minWidth: 155,
      sortable: false,
      showSortButtonInHeader: false,
      menuItems: stripHeaderSortControls(effectiveDateColumn?.menuItems),
    }),
    createColumn(remarksColumn, {
      field: 'remarks',
      header: 'Reason',
      order: 10,
      width: 260,
      minWidth: 260,
      sortable: false,
      showSortButtonInHeader: false,
      menuItems: stripHeaderSortControls(remarksColumn?.menuItems),
    }),
    createColumn(approverInfoColumn, {
      field: 'approverInfo',
      header: 'Approvers',
      order: 11,
      width: 190,
      minWidth: 190,
      format: undefined,
      filter: false,
      sortable: false,
      showSortButtonInHeader: false,
      showFilterButtonInHeader: false,
      isGlobalFilter: false,
      menuItems: [],
    }),
    createColumn(requestStatusColumn, {
      field: 'requestStatusInfo',
      header: 'Request Status',
      order: 12,
      width: 165,
      minWidth: 165,
      format: undefined,
      sortable: false,
      showSortButtonInHeader: false,
      menuItems: stripHeaderSortControls(requestStatusColumn?.menuItems),
    }),
    createColumn(approverInfoColumn, {
      field: 'approvedByDisplay',
      header: 'Approved By',
      order: 13,
      width: 190,
      minWidth: 190,
      format: undefined,
      filter: true,
      sortable: false,
      showSortButtonInHeader: false,
      showFilterButtonInHeader: false,
      isGlobalFilter: true,
      menuItems: approvedByMenuItems,
    }),
    createColumn(approverInfoColumn, {
      field: 'approvedDateTimeDisplay',
      header: 'Approved Date / Time',
      order: 14,
      width: 190,
      minWidth: 190,
      format: undefined,
      filter: true,
      sortable: false,
      showSortButtonInHeader: false,
      showFilterButtonInHeader: false,
      isGlobalFilter: true,
      menuItems: approvedDateMenuItems,
    }),
    createColumn(approveRemarksColumn, {
      field: 'approvedRemarks',
      header: 'Approve Remarks',
      order: 15,
      width: 240,
      minWidth: 240,
      sortable: false,
      showSortButtonInHeader: false,
      menuItems: stripHeaderSortControls(approveRemarksColumn?.menuItems),
    }),
    ...(canShowActionColumn ? [{
      field: 'action',
      header: 'Action',
      headerClassName: actionColumnClassName,
      bodyClassName: actionColumnClassName,
      className: actionColumnClassName,
      order: 16,
      width: actionColumnWidth,
      visible: true,
      defaultPinned: false,
      pinnable: false,
      sortable: false,
      filter: false,
      filterVisible: false,
      showFilterButtonInHeader: false,
      showSortButtonInHeader: false,
      showPinButtonInHeader: false,
      isGlobalFilter: false,
      menuItems: [],
    }] : []),
  ];

  const normalizedHiddenColumns = hiddenColumns.map((column, index) => ({
    ...column,
    order: visibleColumns.length + 1 + index,
  }));

  return [
    ...visibleColumns,
    ...normalizedHiddenColumns,
  ];
};

const buildBaseAdditionalPayload = ({
  employeeId,
  projectId,
  projectSeqId,
  projectName,
  statusChangeId,
}) => {
  const payload = statusChangeId
    ? { statusChangeId }
    : {
      request_status: [0],
      assigned_to_me: false,
    };

  if (employeeId) payload.logged_in_emp_id = employeeId;
  if (projectId) payload.project_id = projectId;
  if (projectSeqId) payload.project_seq_id = projectSeqId;
  if (projectName) payload.project_name = projectName;

  return payload;
};

const sanitizeRequestHistoryPayload = (payload = {}, options = {}) => {
  const { isAllProjectsMode = false, scopedProjectName = '' } = options;
  const nextPayload = { ...(payload || {}) };
  const selectedProjectNames = Array.isArray(nextPayload?.project_name)
    ? nextPayload.project_name
    : (nextPayload?.project_name ? [nextPayload.project_name] : []);
  const normalizedSelectedProjectNames = selectedProjectNames
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const normalizedScopedProjectName = String(scopedProjectName || '')
    .trim()
    .toLowerCase();

  if (isAllProjectsMode) {
    delete nextPayload.project_id;
    delete nextPayload.project_seq_id;
  } else if (normalizedSelectedProjectNames.length > 0) {
    const hasMatchingScopedProject = (
      normalizedSelectedProjectNames.length === 1
      && normalizedScopedProjectName
      && normalizedSelectedProjectNames[0].toLowerCase() === normalizedScopedProjectName
    );

    if (!hasMatchingScopedProject) {
      delete nextPayload.project_id;
      delete nextPayload.project_seq_id;
    }
  }

  return nextPayload;
};

const sanitizeHistoryRequestPayload = (payload = {}, options = {}) => {
  return sanitizeRequestHistoryPayload(payload, options);
};

const getAppliedAdditionalPayload = (tableConfiguration = [], basePayload = {}, options = {}) => {
  const {
    preserveBaseProjectScope = false,
  } = options;
  const nextPayload = { ...basePayload };
  const projectNameFilter = getFilterItem(tableConfiguration, 'projectName');
  const projectVerticalFilter = getFilterItem(tableConfiguration, 'projectVertical');
  const projectTypeFilter = getFilterItem(tableConfiguration, 'projectType');
  const requestedByFilter = getFilterItem(tableConfiguration, 'requestedBy');
  const approvedByFilter = getFilterItem(tableConfiguration, 'approvedBy');
  const requestStatusFilter = getFilterItem(
    tableConfiguration,
    [REQUEST_STATUS_FILTER_FIELD, 'requestStatus'],
  );
  const requestedDateFilter = getFilterItem(tableConfiguration, 'requestedDate');
  const assignedToMeFilter = getFilterItem(
    tableConfiguration,
    [ASSIGNED_TO_ME_FILTER_FIELD, 'assignedToMe'],
  );

  const projectNameValues = normalizeFilterValues(projectNameFilter?.selectedValues);
  if (projectNameValues.length) {
    nextPayload.project_name = projectNameValues;
    if (!preserveBaseProjectScope && !basePayload?.project_id) {
      delete nextPayload.project_id;
      delete nextPayload.project_seq_id;
    }
  } else if (!preserveBaseProjectScope) {
    delete nextPayload.project_name;
    delete nextPayload.project_id;
    delete nextPayload.project_seq_id;
  }

  const projectVerticalValues = normalizeFilterValues(projectVerticalFilter?.selectedValues);
  if (projectVerticalValues.length) {
    nextPayload.project_vertical = projectVerticalValues;
  } else {
    delete nextPayload.project_vertical;
  }

  const projectTypeValues = normalizeFilterValues(projectTypeFilter?.selectedValues);
  if (projectTypeValues.length) {
    nextPayload.project_type = projectTypeValues;
  } else {
    delete nextPayload.project_type;
  }

  const requestedByValues = normalizeFilterValues(requestedByFilter?.selectedValues);
  if (requestedByValues.length) {
    nextPayload.requested_by = requestedByValues;
  } else {
    delete nextPayload.requested_by;
  }

  const approvedByValues = normalizeFilterValues(approvedByFilter?.selectedValues)
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  if (approvedByValues.length) {
    nextPayload.approved_by = approvedByValues;
  } else {
    delete nextPayload.approved_by;
  }

  const requestStatusValues = normalizeFilterValues(requestStatusFilter?.selectedValues);
  if (requestStatusValues.length) {
    nextPayload.request_status = requestStatusValues;
  } else {
    delete nextPayload.request_status;
  }

  const [requestedDateFrom, requestedDateTo] = Array.isArray(requestedDateFilter?.range)
    ? requestedDateFilter.range
    : [null, null];

  if (requestedDateFrom) {
    nextPayload.requested_date_from = requestedDateFrom;
  } else {
    delete nextPayload.requested_date_from;
  }

  if (requestedDateTo) {
    nextPayload.requested_date_to = requestedDateTo;
  } else {
    delete nextPayload.requested_date_to;
  }

  const assignedToMeValues = normalizeFilterValues(assignedToMeFilter?.selectedValues);
  if (assignedToMeFilter) {
    const isAssignedToMeSelected = assignedToMeValues.includes(true)
      || assignedToMeValues.includes('true')
      || assignedToMeValues.includes(1)
      || assignedToMeValues.includes('1')
      || assignedToMeValues.includes('Assigned to me');
    nextPayload.assigned_to_me = isAssignedToMeSelected;
  } else {
    delete nextPayload.assigned_to_me;
  }

  return nextPayload;
};

const arePayloadsEqual = (left = {}, right = {}) => JSON.stringify(left) === JSON.stringify(right);

const normalizeRequestHistoryResponse = (response, context = {}) => {
  if (!response || typeof response !== 'object') return response;

  const rawConfiguration = response.data?.tableConfiguration
    ?? response.data?.data?.tableConfiguration
    ?? response.tableConfiguration
    ?? response.tableConfig
    ?? [];
  const rawRows = response.data?.tableData
    ?? response.data?.data?.tableData
    ?? response.tableData
    ?? response.data?.data
    ?? [];

  const remappedConfiguration = enhanceRequestHistoryColumns(
    remapStatusFilters(rawConfiguration, context),
    context,
  );
  const enrichedRows = Array.isArray(rawRows)
    ? rawRows.map((row) => {
      const requestShareUrl = buildStatusRequestShareUrl(row);
      const enrichedRow = {
        ...row,
        requestShareUrl,
      };

      return {
        ...enrichedRow,
        scrn: row?.scrn || row?.srNumber || '-',
        [ASSIGNED_TO_ME_FILTER_FIELD]: 'Assigned to me',
        selection: <SelectionRowCell row={row} />,
        srNumber: <ScrnRowCell row={row} onScrnClick={context.onScrnClick} />,
        requestedByDisplay: renderPersonCell({
          name: row?.requestedByName || row?.requesterName || row?.requestedBy,
          empId: row?.requesterEmpId,
          email: row?.requesterEmail,
        }, 'Requested by'),
        requestedDateDisplay: row?.requestedDateTime
          || [row?.requestedDate, row?.requestedTime].filter(Boolean).join(' '),
        projectNameDisplay: renderTruncatedTextCell(
          [row?.projectId, row?.projectName].filter(Boolean).join(' - ')
          || row?.projectName
          || '-',
        ),
        projectVerticalDisplay: renderTruncatedTextCell(row?.projectVertical || row?.project_vertical || '-'),
        oldStatusDisplay: renderProjectStatusCell(row?.oldStatus || row?.old_status || '-'),
        newStatusDisplay: renderProjectStatusCell(row?.newStatus || row?.new_status || '-'),
        approverInfo: renderPersonCell({
          name: row?.approverName || row?.approvedBy,
          empId: row?.approverEmpId || row?.approvedEmpId,
          email: row?.approverEmail,
        }, 'Approver', {
          name: row?.approverName || row?.approvedBy,
          empId: row?.approverEmpId || row?.approvedEmpId,
          email: row?.approverEmail,
        }),
        remarks: renderTruncatedTextCell(row?.remarks || row?.reason || '-'),
        requestStatusInfo: renderRequestStatusCell(row, context),
        approvedByDisplay: renderApprovedByCell(row),
        approvedDateTimeDisplay: renderApprovedDateTimeCell(row),
        approvedRemarks: renderTruncatedTextCell(row?.approvedRemarks || row?.approved_remarks || '-'),
        action: renderActionCell(enrichedRow, context),
        rowConfiguration: {
          ...(row?.rowConfiguration || {}),
          onClick: [],
        },
      };
    })
    : rawRows;

  const styledRows = applyFormatStylesToRows(remappedConfiguration, enrichedRows);
  const pagination = response.meta
    ?? response.pagination
    ?? response.paginationConfig
    ?? response.data?.meta
    ?? response.data?.pagination
    ?? response.data?.paginationConfig
    ?? response.data?.data?.meta
    ?? response.data?.data?.pagination
    ?? response.data?.data?.paginationConfig
    ?? null;
  const resolvedMeta = {
    ...(response.meta || {}),
    page: pagination?.page ?? response.meta?.page ?? 1,
    limit: pagination?.limit ?? response.meta?.limit ?? 10,
    total:
      pagination?.total
      ?? pagination?.count
      ?? response.meta?.total
      ?? (Array.isArray(rawRows) ? rawRows.length : 0),
  };

  response.meta = resolvedMeta;
  response.data = {
    ...(response.data || {}),
    tableConfiguration: remappedConfiguration,
    tableData: styledRows,
    meta: {
      ...(response.data?.meta || {}),
      ...resolvedMeta,
    },
    data: {
      ...(response.data?.data || {}),
      tableConfiguration: remappedConfiguration,
      tableData: styledRows,
      meta: {
        ...(response.data?.data?.meta || {}),
        ...resolvedMeta,
      },
    },
  };

  normalizeReqTableResponse(response);

  return response;
};

const withAccessTokenHeaders = (request, accessToken) => ({
  ...request,
  headers: {
    ...(request?.headers || {}),
    token: accessToken,
    isAuthToken: true,
  },
});

const RequestHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const { projectData, loading: projectDetailsLoading, refetchProjectData } = useProjectDetailsData({
    queryKey: 'projectSeqId',
    fallbackKeys: ['projectId', 'id'],
  });
  const { snackbarOpen } = useSnackbar();

  const resolvedProfile = useMemo(
    () => (userProfile && !Array.isArray(userProfile) ? userProfile : null),
    [userProfile],
  );
  const permissions = resolvedProfile?.permissions || [];
  const moduleActions = useMemo(() => {
    if (Array.isArray(permissions)) {
      const modulePermission = permissions.find(
        (permission) => permission?.module === MODULE_NAMES.DELIVERY_PROJECT_STATUS,
      );
      return Array.isArray(modulePermission?.actions) ? modulePermission.actions : [];
    }

    if (permissions && typeof permissions === 'object') {
      const legacyActions = permissions[MODULE_NAMES.DELIVERY_PROJECT_STATUS];
      return Array.isArray(legacyActions) ? legacyActions : [];
    }

    return [];
  }, [permissions]);
  const approvalActions = useMemo(() => {
    if (Array.isArray(permissions)) {
      const modulePermission = permissions.find(
        (permission) => permission?.module === MODULE_NAMES.DELIVERY_STATUS_HISTORY_APPROVAL,
      );
      return Array.isArray(modulePermission?.actions) ? modulePermission.actions : [];
    }

    if (permissions && typeof permissions === 'object') {
      const legacyActions = permissions[MODULE_NAMES.DELIVERY_STATUS_HISTORY_APPROVAL];
      return Array.isArray(legacyActions) ? legacyActions : [];
    }

    return [];
  }, [permissions]);
  const isSuperAdmin = isSuperAdminUser(resolvedProfile);
  const normalizedModuleActions = useMemo(
    () => moduleActions.map(normalizePermissionAction),
    [moduleActions],
  );
  const normalizedApprovalActions = useMemo(
    () => approvalActions.map(normalizePermissionAction),
    [approvalActions],
  );
  const canView = isSuperAdmin || normalizedModuleActions.includes('read');
  const canCreate = isSuperAdmin || normalizedModuleActions.includes('create');
  const canUpdate = isSuperAdmin || normalizedModuleActions.includes('update');
  const canDelete = isSuperAdmin || normalizedModuleActions.includes('delete');
  const canShareLink = canView && canCreate && canUpdate && canDelete;
  const canViewStatus = isSuperAdmin || normalizedApprovalActions.includes('read');
  const canUpdateStatus = isSuperAdmin || normalizedApprovalActions.includes('update');
  const canReadStatusColumn = canView || canViewStatus;
  const disableCreateActions = !canCreate;
  const disableUpdateActions = !canUpdate;
  const disableDeleteActions = !canDelete;
  const disableUpdateStatusActions = !canUpdateStatus;
  const canShowActionColumn = canUpdate || canDelete;
  const hasSingleActionColumn = canShowActionColumn && !(canUpdate && canDelete);

  const employeeId = useMemo(
    () => (
      resolvedProfile?.employeeId
      ?? resolvedProfile?.emp_id
      ?? resolvedProfile?.empId
      ?? resolvedProfile?.emp_no
      ?? resolvedProfile?.empNo
      ?? null
    ),
    [resolvedProfile],
  );
  const employeeNo = useMemo(
    () => getResolvedEmployeeNumber(resolvedProfile),
    [resolvedProfile],
  );

  const accessToken = useMemo(
    () => (
      resolvedProfile?.access_token
      ?? resolvedProfile?.accessToken
      ?? resolvedProfile?.token
      ?? null
    ),
    [resolvedProfile],
  );

  const routeState = useMemo(
    () => (location.state && typeof location.state === 'object' ? location.state : {}),
    [location.state],
  );

  const routeProjectSeqId = useMemo(
    () => routeState?.projectSeqId ?? null,
    [routeState],
  );

  const routeProjectId = useMemo(
    () => routeState?.projectId ?? null,
    [routeState],
  );

  const queryProjectName = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return String(params.get('projectName') || '').trim();
  }, [location.search]);

  const routeProjectName = useMemo(
    () => String(routeState?.projectName || queryProjectName || '').trim(),
    [routeState, queryProjectName],
  );
  const requestHistoryOriginContextKey = useMemo(
    () => [REQUEST_HISTORY_ORIGIN_CONTEXT_KEY, employeeId || 'anonymous'].join(':'),
    [employeeId],
  );
  const storedOriginContext = useMemo(
    () => safeReadSessionJson(requestHistoryOriginContextKey) || {},
    [requestHistoryOriginContextKey],
  );

  const queryProjectSeqId = useMemo(
    () => getProjectSeqIdFromLocation(location.search),
    [location.search],
  );

  const queryProjectId = useMemo(
    () => getProjectIdFromLocation(location.search),
    [location.search],
  );

  const statusChangeId = useMemo(
    () => getStatusChangeIdFromLocation(location.search),
    [location.search],
  );

  const requestHistoryMode = useMemo(
    () => getModeFromLocation(location.search),
    [location.search],
  );
  const isAllProjectsMode = requestHistoryMode === 'all';

  const isFromNotification = useMemo(
    () => isFromNotificationFromLocation(location.search),
    [location.search],
  );

  const projectId = useMemo(
    () => (
      isAllProjectsMode
        ? null
        : routeProjectId
        ?? queryProjectId
        ?? projectData?.project_id
        ?? projectData?.projectId
        ?? null
    ),
    [isAllProjectsMode, projectData, queryProjectId, routeProjectId],
  );

  const projectName = useMemo(
    () => (
      isAllProjectsMode
        ? ''
        : routeProjectName
        || projectData?.project_name
        || projectData?.projectName
        || ''
    ),
    [isAllProjectsMode, projectData, routeProjectName],
  );
  const projectSeqId = useMemo(
    () => (
      isAllProjectsMode
        ? null
        : routeProjectSeqId
        ?? queryProjectSeqId
        ?? null
    ),
    [isAllProjectsMode, queryProjectSeqId, routeProjectSeqId],
  );

  const isHardReload = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (navigationEntry && typeof navigationEntry.type === 'string') {
      return navigationEntry.type === 'reload';
    }

    if (window.performance?.navigation) {
      return window.performance.navigation.type === 1;
    }

    return false;
  }, []);
  const originalProjectSeqId = useMemo(
    () => routeProjectSeqId ?? queryProjectSeqId ?? storedOriginContext?.projectSeqId ?? null,
    [queryProjectSeqId, routeProjectSeqId, storedOriginContext?.projectSeqId],
  );

  const originalProjectId = useMemo(
    () => routeProjectId ?? queryProjectId ?? storedOriginContext?.projectId ?? null,
    [queryProjectId, routeProjectId, storedOriginContext?.projectId],
  );
  const originalProjectName = useMemo(
    () => String(routeProjectName || storedOriginContext?.projectName || '').trim(),
    [routeProjectName, storedOriginContext?.projectName],
  );
  const restoredProjectName = useMemo(
    () => String(
      originalProjectName
      || (!isAllProjectsMode ? projectData?.project_name ?? projectData?.projectName ?? '' : ''),
    ).trim(),
    [isAllProjectsMode, originalProjectName, projectData],
  );
  const preservedRouteState = useMemo(() => {
    const nextState = {};
    const resolvedProjectName = restoredProjectName;

    if (originalProjectId) nextState.projectId = String(originalProjectId);
    if (originalProjectSeqId) nextState.projectSeqId = String(originalProjectSeqId);
    if (resolvedProjectName) nextState.projectName = resolvedProjectName;

    return nextState;
  }, [
    originalProjectId,
    originalProjectSeqId,
    restoredProjectName,
  ]);
  const isFreshProjectDetailsEntry = routeState?.requestHistoryEntryMode === 'fresh';

  useEffect(() => {
    void primeProjectStatusOptions();
  }, []);

  useEffect(() => {
    if (!originalProjectId && !originalProjectSeqId && !originalProjectName) {
      return;
    }

    safeWriteSessionJson(requestHistoryOriginContextKey, {
      projectId: originalProjectId ? String(originalProjectId) : '',
      projectSeqId: originalProjectSeqId ? String(originalProjectSeqId) : '',
      projectName: originalProjectName,
    });
  }, [
    originalProjectId,
    originalProjectName,
    originalProjectSeqId,
    requestHistoryOriginContextKey,
  ]);

  const projectDetailsReturnUrl = useMemo(() => {
    const params = new URLSearchParams();

    if (originalProjectSeqId) {
      params.set('projectSeqId', String(originalProjectSeqId));
    } else if (originalProjectId) {
      params.set('projectId', String(originalProjectId));
    }

    const queryString = params.toString();
    return `/delivery/project-details${queryString ? `?${queryString}` : ''}`;
  }, [originalProjectId, originalProjectSeqId]);

  const requestHistoryStorageKey = useMemo(
    () => [
      'request-history-page',
      REQUEST_HISTORY_TABLE_STATE_VERSION,
      employeeId,
      requestHistoryMode || 'project',
      originalProjectSeqId || originalProjectId || statusChangeId || 'default',
    ].filter(Boolean).join(':'),
    [employeeId, originalProjectId, originalProjectSeqId, requestHistoryMode, statusChangeId],
  );
  const requestHistoryTableStateKey = `${requestHistoryStorageKey}:table-state`;
  const resetStorageKey = useMemo(
    () => [
      'request-history-page',
      REQUEST_HISTORY_TABLE_STATE_VERSION,
      employeeId,
      'project',
      originalProjectSeqId || originalProjectId || statusChangeId || 'default',
    ].filter(Boolean).join(':'),
    [employeeId, originalProjectId, originalProjectSeqId, statusChangeId],
  );
  const resetTableStateKey = `${resetStorageKey}:table-state`;
  const cachedRequestHistoryState = useMemo(
    () => {
      if (isHardReload || isFreshProjectDetailsEntry) {
        safeRemoveSessionItem(requestHistoryStorageKey);
        safeRemoveSessionItem(requestHistoryTableStateKey);
        return {};
      }

      return safeReadSessionJson(requestHistoryStorageKey) || {};
    },
    [
      isFreshProjectDetailsEntry,
      isHardReload,
      requestHistoryStorageKey,
      requestHistoryTableStateKey,
    ],
  );
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addBlocked, setAddBlocked] = useState(false);
  const [addBlockedMessage, setAddBlockedMessage] = useState('');
  const [checkingAddAvailability, setCheckingAddAvailability] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRequestLoading, setEditRequestLoading] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [bulkStatusRows, setBulkStatusRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState(() => (
    Array.isArray(cachedRequestHistoryState?.selectedRowIds)
      ? cachedRequestHistoryState.selectedRowIds
      : []
  ));
  const [visibleRowIds, setVisibleRowIds] = useState(() => (
    Array.isArray(cachedRequestHistoryState?.visibleRowIds)
      ? cachedRequestHistoryState.visibleRowIds
      : []
  ));
  const [visibleRows, setVisibleRows] = useState(() => (
    Array.isArray(cachedRequestHistoryState?.visibleRows)
      ? cachedRequestHistoryState.visibleRows
      : []
  ));
  const [isResetRestoring, setIsResetRestoring] = useState(false);
  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [tableRefreshNonce, setTableRefreshNonce] = useState(0);
  const hasConsumedInitialProjectSeedRef = useRef(false);
  const hasConsumedReqStatusSeedRef = useRef(false);
  const hasConsumedAssignedToMeSeedRef = useRef(false);
  const hasConsumedRequestedDateSeedRef = useRef(false);
  const suppressDefaultSeedStateRef = useRef(false);
  const previousProjectFilterSelectedRef = useRef(null);
  const pendingHistoryUrlRef = useRef('');
  const pendingResetUrlRef = useRef('');
  const pendingResetPayloadRef = useRef(null);
  const pendingResetLikeResponseRef = useRef(false);
  const transientCancelRefreshRef = useRef(null);
  const lastTableSnapshotRef = useRef(cachedRequestHistoryState?.lastTableSnapshot || null);
  const forceDefaultUiSeedsRef = useRef(!statusChangeId);
  const approvedByFilterOptionsRef = useRef(buildApprovedByFilterOptions(
    Array.isArray(cachedRequestHistoryState?.visibleRows) ? cachedRequestHistoryState.visibleRows : [],
  ));

  const baseAdditionalPayloadData = useMemo(
    () => buildBaseAdditionalPayload({
      employeeId,
      projectId,
      projectSeqId,
      projectName,
      statusChangeId,
    }),
    [
      employeeId,
      projectId,
      projectName,
      projectSeqId,
      statusChangeId,
    ],
  );
  const initialResetProjectName = useMemo(
    () => String(
      originalProjectName
      || routeProjectName
      || projectData?.project_name
      || projectData?.projectName
      || '',
    ).trim(),
    [originalProjectName, projectData, routeProjectName],
  );
  const cachedAdditionalPayloadData = useMemo(
    () => sanitizeRequestHistoryPayload(cachedRequestHistoryState?.additionalPayloadData, {
      isAllProjectsMode,
      scopedProjectName: initialResetProjectName || projectName,
    }),
    [
      cachedRequestHistoryState?.additionalPayloadData,
      initialResetProjectName,
      isAllProjectsMode,
      projectName,
    ],
  );
  const hasCachedAdditionalPayloadData = useMemo(
    () => (
      cachedAdditionalPayloadData
      && typeof cachedAdditionalPayloadData === 'object'
      && Object.keys(cachedAdditionalPayloadData).length > 0
    ),
    [cachedAdditionalPayloadData],
  );
  const initialResetPayloadData = useMemo(
    () => buildBaseAdditionalPayload({
      employeeId,
      projectId: originalProjectId ?? projectId,
      projectSeqId: originalProjectSeqId ?? projectSeqId,
      projectName: initialResetProjectName || projectName,
      statusChangeId,
    }),
    [
      employeeId,
      initialResetProjectName,
      originalProjectId,
      originalProjectSeqId,
      projectId,
      projectName,
      projectSeqId,
      statusChangeId,
    ],
  );
  const mutationUserInfo = useMemo(
    () => ({
      employeeId,
      employeeNo,
      emailId: getResolvedEmployeeEmail(resolvedProfile),
      employeeName: getResolvedEmployeeName(resolvedProfile),
    }),
    [employeeId, employeeNo, resolvedProfile],
  );
  const requestActorId = useMemo(
    () => mutationUserInfo.employeeNo || mutationUserInfo.employeeId || mutationUserInfo.emailId || '',
    [mutationUserInfo],
  );
  const createAuthorizedRequest = useCallback(
    (request) => withAccessTokenHeaders(request, accessToken),
    [accessToken],
  );
  const shouldUseInitialResetPayload = useMemo(
    () => (
      !isAllProjectsMode
      && Boolean(originalProjectId || originalProjectSeqId || initialResetProjectName)
    ),
    [initialResetProjectName, isAllProjectsMode, originalProjectId, originalProjectSeqId],
  );

  const [additionalPayloadData, setAdditionalPayloadData] = useState(() => (
    hasCachedAdditionalPayloadData
      ? cachedAdditionalPayloadData
      : (
        shouldUseInitialResetPayload
          ? initialResetPayloadData
          : baseAdditionalPayloadData
      )
  ));
  const tableRequestPayload = useMemo(
    () => sanitizeHistoryRequestPayload(additionalPayloadData, {
      isAllProjectsMode,
      scopedProjectName: initialResetProjectName || projectName,
    }),
    [additionalPayloadData, initialResetProjectName, isAllProjectsMode, projectName],
  );
  const selectedProjectNameValues = useMemo(
    () => normalizeFilterValues(additionalPayloadData?.project_name)
      .map((value) => String(value || '').trim())
      .filter(Boolean),
    [additionalPayloadData],
  );
  const isProjectNameSelected = selectedProjectNameValues.length > 0;
  const hasSingleProjectNameSelected = selectedProjectNameValues.length === 1;
  const selectedProjectNameLabel = useMemo(() => {
    return selectedProjectNameValues[0] || '';
  }, [selectedProjectNameValues]);
  const selectedProjectContext = useMemo(() => {
    if (!hasSingleProjectNameSelected || !selectedProjectNameLabel) {
      return null;
    }

    const normalizedSelectedProjectName = selectedProjectNameLabel.trim().toLowerCase();
    const matchingVisibleRows = visibleRows.filter((row) => (
      String(row?.projectName ?? row?.project_name ?? '')
        .trim()
        .toLowerCase() === normalizedSelectedProjectName
    ));

    const uniqueProjectContexts = matchingVisibleRows.reduce((acc, row) => {
      const rowProjectId = row?.projectId ?? row?.project_id;
      if (!rowProjectId) return acc;

      const contextKey = String(rowProjectId);
      if (acc.some((candidate) => candidate.key === contextKey)) {
        return acc;
      }

      return [
        ...acc,
        {
          key: contextKey,
          projectId: rowProjectId,
          projectSeqId: row?.projectSeqId ?? row?.project_seq_id ?? rowProjectId,
          projectName: row?.projectName ?? row?.project_name ?? selectedProjectNameLabel,
        },
      ];
    }, []);

    if (uniqueProjectContexts.length === 1) {
      const [resolvedContext] = uniqueProjectContexts;
      return {
        projectId: resolvedContext.projectId,
        projectSeqId: resolvedContext.projectSeqId,
        projectName: resolvedContext.projectName,
      };
    }

    const normalizedOriginalProjectName = String(
      routeProjectName
      || projectData?.project_name
      || projectData?.projectName
      || '',
    )
      .trim()
      .toLowerCase();

    if (normalizedOriginalProjectName === normalizedSelectedProjectName && originalProjectId) {
      return {
        projectId: originalProjectId,
        projectSeqId: originalProjectSeqId || originalProjectId,
        projectName: selectedProjectNameLabel,
      };
    }

    return null;
  }, [
    hasSingleProjectNameSelected,
    originalProjectId,
    originalProjectSeqId,
    projectData,
    routeProjectName,
    selectedProjectNameLabel,
    visibleRows,
  ]);
  const selectedProjectCurrentStatus = useMemo(() => {
    const normalizedSelectedProjectName = String(selectedProjectNameLabel || '')
      .trim()
      .toLowerCase();

    if (!normalizedSelectedProjectName) {
      return '';
    }

    const normalizedPageProjectName = String(
      routeProjectName
      || projectData?.project_name
      || projectData?.projectName
      || '',
    )
      .trim()
      .toLowerCase();

    if (normalizedPageProjectName && normalizedPageProjectName === normalizedSelectedProjectName) {
      return String(projectData?.project_status || projectData?.projectStatus || '').trim();
    }

    return '';
  }, [projectData, routeProjectName, selectedProjectNameLabel]);
  const selectedRows = useMemo(
    () => visibleRows.filter((row) => selectedRowIds.includes(getRowSelectionId(row))),
    [selectedRowIds, visibleRows],
  );
  const actionableSelectedRows = useMemo(
    () => selectedRows.filter((row) => isRequestActionable(row) && row?.statusChangeId),
    [selectedRows],
  );

  useEffect(() => {
    setAdditionalPayloadData((currentPayload) => {
      const nextPayload = {
        ...currentPayload,
        ...baseAdditionalPayloadData,
      };

      if (currentPayload?.project_name) {
        nextPayload.project_name = currentPayload.project_name;
      }

      if (currentPayload?.project_vertical) {
        nextPayload.project_vertical = currentPayload.project_vertical;
      }

      if (currentPayload?.project_type) {
        nextPayload.project_type = currentPayload.project_type;
      }

      if (currentPayload?.requested_by) {
        nextPayload.requested_by = currentPayload.requested_by;
      }

      if (currentPayload?.request_status) {
        nextPayload.request_status = currentPayload.request_status;
      }

      if (currentPayload?.requested_date_from) {
        nextPayload.requested_date_from = currentPayload.requested_date_from;
      }

      if (currentPayload?.requested_date_to) {
        nextPayload.requested_date_to = currentPayload.requested_date_to;
      }

      if (Object.prototype.hasOwnProperty.call(currentPayload || {}, 'assigned_to_me')) {
        nextPayload.assigned_to_me = Boolean(currentPayload.assigned_to_me);
      }

      const shouldPreserveInitialProjectScope = (
        !hasConsumedInitialProjectSeedRef.current
        && Boolean(
          baseAdditionalPayloadData?.project_name
          || baseAdditionalPayloadData?.project_id
          || baseAdditionalPayloadData?.project_seq_id,
        )
      );
      const currentProjectSelections = normalizeFilterValues(currentPayload?.project_name)
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      const baseProjectSelections = normalizeFilterValues(baseAdditionalPayloadData?.project_name)
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      const selectedProjectMatchesBase = (
        currentProjectSelections.length === 1
        && baseProjectSelections.length === 1
        && currentProjectSelections[0].toLowerCase() === baseProjectSelections[0].toLowerCase()
      );

      if (currentProjectSelections.length > 0 && !selectedProjectMatchesBase) {
        delete nextPayload.project_id;
        delete nextPayload.project_seq_id;
      }

      if (isAllProjectsMode) {
        delete nextPayload.project_id;
        delete nextPayload.project_seq_id;
      } else if (
        !currentPayload?.project_name
        && !shouldPreserveInitialProjectScope
      ) {
        delete nextPayload.project_name;
        delete nextPayload.project_id;
        delete nextPayload.project_seq_id;
      }

      const sanitizedNextPayload = sanitizeRequestHistoryPayload(nextPayload, {
        isAllProjectsMode,
        scopedProjectName: initialResetProjectName || projectName,
      });
      return arePayloadsEqual(currentPayload, sanitizedNextPayload) ? currentPayload : sanitizedNextPayload;
    });
  }, [baseAdditionalPayloadData, initialResetProjectName, isAllProjectsMode, projectName]);

  useEffect(() => {
    safeWriteSessionJson(requestHistoryStorageKey, {
      additionalPayloadData: sanitizeRequestHistoryPayload(additionalPayloadData, {
        isAllProjectsMode,
        scopedProjectName: initialResetProjectName || projectName,
      }),
      visibleRows,
      visibleRowIds,
      selectedRowIds,
      lastTableSnapshot: lastTableSnapshotRef.current,
    });
  }, [
    additionalPayloadData,
    initialResetProjectName,
    isAllProjectsMode,
    projectName,
    requestHistoryStorageKey,
    selectedRowIds,
    visibleRowIds,
    visibleRows,
  ]);

  useEffect(() => {
    pendingHistoryUrlRef.current = '';
  }, [location.search]);

  useEffect(() => {
    previousProjectFilterSelectedRef.current = isProjectNameSelected;
  }, [isProjectNameSelected]);

  const currentHistoryUrl = useMemo(
    () => `${location.pathname}${location.search || ''}`,
    [location.pathname, location.search],
  );

  useEffect(() => {
    if (!isResetRestoring) {
      return;
    }

    const pendingResetUrl = pendingResetUrlRef.current;
    const pendingResetPayload = pendingResetPayloadRef.current;
    if (!pendingResetUrl || currentHistoryUrl !== pendingResetUrl) {
      return;
    }

    if (
      pendingResetPayload
      && !arePayloadsEqual(additionalPayloadData, pendingResetPayload)
    ) {
      return;
    }

    pendingResetUrlRef.current = '';
    pendingResetPayloadRef.current = null;
    pendingResetLikeResponseRef.current = false;
    setTableRefreshNonce((current) => current + 1);
    setIsResetRestoring(false);
  }, [additionalPayloadData, currentHistoryUrl, isResetRestoring]);

  const startResetRestore = useCallback((targetUrl, nextPayload) => {
    pendingResetUrlRef.current = targetUrl;
    pendingResetPayloadRef.current = nextPayload;
    forceDefaultUiSeedsRef.current = true;
    setIsResetRestoring(true);
    setAdditionalPayloadData(nextPayload);
  }, []);

  useEffect(() => {
    if (!isHardReload || !isAllProjectsMode || statusChangeId || !originalProjectId) {
      return;
    }

    const targetUrl = buildRequestHistoryUrl({ projectId: originalProjectId });
    pendingResetLikeResponseRef.current = true;
    startResetRestore(targetUrl, initialResetPayloadData);
    navigate(
      targetUrl,
      {
        replace: true,
        state: preservedRouteState,
      },
    );
  }, [
    initialResetPayloadData,
    isAllProjectsMode,
    isHardReload,
    navigate,
    originalProjectId,
    preservedRouteState,
    startResetRestore,
    statusChangeId,
  ]);

  useEffect(() => {
    [EditActionIcon, DeleteActionIcon, CopyActionIcon].forEach((src) => {
      if (!src) return;
      const image = new Image();
      image.src = src;
    });
  }, []);

  const handleClose = useCallback(() => {
    navigate(projectDetailsReturnUrl, { state: preservedRouteState });
  }, [navigate, preservedRouteState, projectDetailsReturnUrl]);

  const openViewRequest = useCallback((rowData) => {
    setSelectedRequest(rowData || null);
    setViewOpen(true);
  }, []);

  const refreshTable = useCallback(() => {
    setTableRefreshNonce((current) => current + 1);
    setSelectedRowIds([]);
    setBulkStatusRows([]);
  }, []);

  const handleResetToInitialState = useCallback(() => {
    hasConsumedInitialProjectSeedRef.current = false;
    hasConsumedReqStatusSeedRef.current = false;
    hasConsumedAssignedToMeSeedRef.current = false;
    hasConsumedRequestedDateSeedRef.current = false;
    suppressDefaultSeedStateRef.current = false;
    previousProjectFilterSelectedRef.current = null;
    transientCancelRefreshRef.current = null;
    approvedByFilterOptionsRef.current = [];
    lastTableSnapshotRef.current = null;

    safeRemoveSessionItem(requestHistoryStorageKey);
    safeRemoveSessionItem(requestHistoryTableStateKey);
    safeRemoveSessionItem(resetStorageKey);
    safeRemoveSessionItem(resetTableStateKey);

    setSelectedRowIds([]);
    setVisibleRowIds([]);
    setVisibleRows([]);
    setBulkStatusRows([]);
    const targetUrl = originalProjectId
      ? buildRequestHistoryUrl({ projectId: originalProjectId })
      : buildRequestHistoryUrl({ mode: 'all' });
    startResetRestore(targetUrl, initialResetPayloadData);

    if (originalProjectId) {
      navigate(
        targetUrl,
        {
          replace: true,
          state: preservedRouteState,
        },
      );
    } else {
      navigate(
        targetUrl,
        {
          replace: true,
          state: preservedRouteState,
        },
      );
    }
  }, [
    initialResetPayloadData,
    navigate,
    originalProjectId,
    preservedRouteState,
    resetStorageKey,
    resetTableStateKey,
    requestHistoryStorageKey,
    requestHistoryTableStateKey,
    startResetRestore,
  ]);

  const handleToggleRowSelect = useCallback((rowId, checked) => {
    if (disableUpdateStatusActions || !rowId) return;
    setSelectedRowIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return Array.from(next);
    });
  }, [disableUpdateStatusActions]);

  const handleToggleSelectAll = useCallback((checked) => {
    if (disableUpdateStatusActions) return;
    setSelectedRowIds(checked ? visibleRowIds : []);
  }, [disableUpdateStatusActions, visibleRowIds]);

  const openBulkStatusRequest = useCallback(() => {
    if (disableUpdateStatusActions) return;
    if (!actionableSelectedRows.length) {
      snackbarOpen('Select at least one open request to approve or reject.', 'warning');
      return;
    }

    setBulkStatusRows(actionableSelectedRows);
    setSelectedRequest({
      scrn: `${actionableSelectedRows.length} selected request${actionableSelectedRows.length > 1 ? 's' : ''}`,
    });
    setStatusOpen(true);
  }, [actionableSelectedRows, disableUpdateStatusActions, snackbarOpen]);

  const handleOpenSelected = useCallback(() => {
    openBulkStatusRequest();
  }, [openBulkStatusRequest]);

  const buildHistoryListPayload = useCallback((requestStatusValues = [0], scopedProjectContext = null) => {
    const scopedProjectId = scopedProjectContext?.projectId ?? projectId;
    const scopedProjectName = String(
      scopedProjectContext?.projectName ?? projectName ?? '',
    ).trim();
    const scopedProjectSeqId = scopedProjectContext?.projectSeqId
      ?? projectSeqId
      ?? scopedProjectId
      ?? '';

    return {
      project_id: scopedProjectId,
      project_seq_id: scopedProjectSeqId ? String(scopedProjectSeqId) : '',
      project_name: scopedProjectName,
      logged_in_emp_id: employeeId,
      assigned_to_me: false,
      request_status: requestStatusValues,
      searchQuery: '',
      selectedViewId: null,
      isTreeTable: false,
      pagination: {
        page: 1,
        limit: 10,
      },
      filter: [
        {
          field: 'projectName',
          selectedValues: scopedProjectName ? [scopedProjectName] : [],
          isGlobalFilter: true,
        },
        {
          field: 'requestStatus',
          selectedValues: requestStatusValues,
          isGlobalFilter: true,
        },
      ],
      userInfo: {
        emailId: getResolvedEmployeeEmail(resolvedProfile),
        employeeId,
        employeeNo,
        employeeName: getResolvedEmployeeName(resolvedProfile),
        globalColors: resolvedProfile?.siteConfiguration?.global_colors,
      },
    };
  }, [employeeId, employeeNo, projectId, projectName, projectSeqId, resolvedProfile]);

  const openAddRequestModal = useCallback(async () => {
    if (disableCreateActions || !hasSingleProjectNameSelected) return;

    if (!selectedProjectContext?.projectId || !selectedProjectContext?.projectName) {
      snackbarOpen('Unable to resolve selected project.', 'warning');
      return;
    }

    setCheckingAddAvailability(true);
    try {
      const response = await UseAxios(
        createAuthorizedRequest(listStatusChangeRequestHistory(
          buildHistoryListPayload([0], selectedProjectContext),
        )),
      );
      const data = getApiResponseData(response);
      const openRows = Array.isArray(data?.tableData) ? data.tableData : [];
      const hasOpenRequest = openRows.some((row) => Number(row?.isApproved ?? row?.requestStatusValue) === 0);

      setAddBlocked(hasOpenRequest);
      setAddBlockedMessage(hasOpenRequest ? OPEN_REQUEST_BLOCK_MESSAGE : '');
      setAddOpen(true);
    } catch (error) {
      snackbarOpen(getApiErrorMessage(error, 'Failed to verify existing open requests.'), 'error');
    } finally {
      setCheckingAddAvailability(false);
    }
  }, [
    buildHistoryListPayload,
    createAuthorizedRequest,
    disableCreateActions,
    hasSingleProjectNameSelected,
    selectedProjectContext,
    snackbarOpen,
  ]);

  const openEditRequest = useCallback(async (row) => {
    if (disableUpdateActions || !row?.statusChangeId || !isRequestEditable(row)) return;

    setSelectedRequest({
      ...row,
      remarks: row?.remarks ?? row?.reason ?? '',
      reason: row?.remarks ?? row?.reason ?? '',
    });
    setEditRequestLoading(true);
    setEditOpen(true);

    try {
      const response = await UseAxios(
        createAuthorizedRequest(getStatusChangeRequestById(row.statusChangeId, {
          loggedInEmpId: employeeId,
        })),
      );
      const data = getApiResponseData(response);

      if (!data || data?.name === 'AxiosError' || data?.isAxiosError) {
        throw data || new Error('Failed to fetch request details.');
      }

      const approvalValue = Number(data?.is_approved ?? data?.isApproved ?? row?.requestStatusValue);
      if (approvalValue !== 0) {
        setEditOpen(false);
        setSelectedRequest(null);
        return;
      }

      setSelectedRequest({
        ...row,
        statusChangeId: data?.status_change_id ?? row?.statusChangeId,
        newStatus: data?.new_status ?? data?.newStatus ?? row?.newStatus ?? '',
        effectiveDate: toIsoDate(data?.effective_date ?? data?.effectiveDate ?? row?.effectiveDate ?? ''),
        remarks: data?.remarks ?? row?.remarks ?? row?.reason ?? '',
        reason: data?.remarks ?? row?.remarks ?? row?.reason ?? '',
      });
    } catch (error) {
      setEditOpen(false);
      setSelectedRequest(null);
      snackbarOpen(getApiErrorMessage(error, 'Failed to fetch request details.'), 'error');
    } finally {
      setEditRequestLoading(false);
    }
  }, [createAuthorizedRequest, disableUpdateActions, employeeId, snackbarOpen]);

  const handleDeleteRequest = useCallback((row) => {
    if (disableDeleteActions || !row?.statusChangeId || !isRequestCancelable(row)) return;
    setSelectedRequest(row || null);
    setCancelOpen(true);
  }, [disableDeleteActions]);

  const handleCopyShareLink = useCallback(async (row) => {
    const shareUrl = toAbsoluteRequestShareUrl(row?.requestShareUrl);
    if (!shareUrl) {
      snackbarOpen('Unable to generate share link for this request.', 'warning');
      return;
    }

    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(shareUrl);
      snackbarOpen('Share link copied successfully.', 'success');
    } catch (error) {
      snackbarOpen('Failed to copy share link.', 'error');
    }
  }, [snackbarOpen]);

  const handleCellClick = useCallback(({ fieldPath, rowData, action }) => {
    if (fieldPath === 'srNumber') {
      openViewRequest(rowData);
      return;
    }

    if (fieldPath !== 'action' || !rowData) {
      return;
    }

    if (action === 'edit') {
      openEditRequest(rowData);
      return;
    }

    if (action === 'delete') {
      handleDeleteRequest(rowData);
    }
  }, [handleDeleteRequest, openEditRequest, openViewRequest]);

  const confirmCancelRequest = useCallback(async () => {
    if (disableDeleteActions || !selectedRequest?.statusChangeId) return;

    setCancelSubmitting(true);
    try {
      const response = await UseAxios(
        createAuthorizedRequest(deleteStatusChangeRequest(selectedRequest.statusChangeId, {
          loggedInEmpId: employeeId,
        })),
      );
      const data = getApiResponseData(response);
      if (data?.name === 'AxiosError' || data?.isAxiosError) {
        throw data;
      }
      snackbarOpen('Request cancelled successfully.', 'success');
      setCancelOpen(false);
      transientCancelRefreshRef.current = {
        statusChangeId: selectedRequest.statusChangeId,
      };
      refreshTable();
    } catch (error) {
      snackbarOpen(getApiErrorMessage(error, 'Failed to cancel request.'), 'error');
    } finally {
      setCancelSubmitting(false);
    }
  }, [
    createAuthorizedRequest,
    disableDeleteActions,
    employeeId,
    refreshTable,
    selectedRequest?.statusChangeId,
    snackbarOpen,
  ]);

  const openStatusRequest = useCallback((row) => {
    if (disableUpdateStatusActions) return;
    if (row && !isRequestActionable(row)) return;
    setBulkStatusRows([]);
    setSelectedRequest(row || null);
    setStatusOpen(true);
  }, [disableUpdateStatusActions]);

  const handleAddSubmit = useCallback(async (formData) => {
    if (disableCreateActions) return false;
    if (!selectedProjectContext?.projectId) {
      snackbarOpen('Select a single Project Name to continue.', 'warning');
      return false;
    }
    try {
      const response = await UseAxios(createAuthorizedRequest(createStatusChangeRequest({
        project_id: selectedProjectContext.projectId,
        new_status: formData?.newStatus,
        effective_date: formData?.effectiveDate,
        remarks: formData?.remarks || formData?.reason || '',
        hidden_status_time: formData?.hiddenStatusTime,
        logged_in_emp_id: employeeId,
        requested_by: requestActorId,
        userInfo: mutationUserInfo,
      })));
      const data = getApiResponseData(response);
      if (data?.name === 'AxiosError' || data?.isAxiosError) {
        throw data;
      }
      snackbarOpen('Status request submitted successfully.', 'success');
      setAddOpen(false);
      setAddBlocked(false);
      setAddBlockedMessage('');
      refreshTable();
      if (!isAllProjectsMode) {
        refetchProjectData();
      }
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to submit status request.');
      if (message === OPEN_REQUEST_BLOCK_MESSAGE) {
        setAddBlocked(true);
        setAddBlockedMessage(message);
      }
      snackbarOpen(message, 'error');
      return false;
    }
  }, [
    createAuthorizedRequest,
    disableCreateActions,
    employeeId,
    isAllProjectsMode,
    mutationUserInfo,
    refetchProjectData,
    refreshTable,
    requestActorId,
    selectedProjectContext,
    snackbarOpen,
  ]);

  const handleEditSubmit = useCallback(async (formData) => {
    if (disableUpdateActions || !selectedRequest?.statusChangeId) return false;

    try {
      const response = await UseAxios(
        createAuthorizedRequest(updateStatusChangeRequest(selectedRequest.statusChangeId, {
          new_status: formData?.newStatus,
          effective_date: formData?.effectiveDate,
          remarks: formData?.remarks || formData?.reason || '',
          requested_date: getCurrentDateString(),
          requested_time: getCurrentTimeString(),
          logged_in_emp_id: employeeId,
          requested_by: requestActorId,
          userInfo: mutationUserInfo,
        })),
      );
      const data = getApiResponseData(response);
      if (data?.name === 'AxiosError' || data?.isAxiosError) {
        throw data;
      }
      snackbarOpen('Request updated successfully.', 'success');
      setEditOpen(false);
      refreshTable();
      return true;
    } catch (error) {
      snackbarOpen(getApiErrorMessage(error, 'Failed to update request.'), 'error');
      return false;
    }
  }, [
    createAuthorizedRequest,
    disableUpdateActions,
    employeeId,
    mutationUserInfo,
    refreshTable,
    requestActorId,
    selectedRequest?.statusChangeId,
    snackbarOpen,
  ]);

  const handleStatusSubmit = useCallback(async (decision, remarks) => {
    if (disableUpdateStatusActions) return false;
    const targetRows = bulkStatusRows.length
      ? bulkStatusRows
      : ((selectedRequest?.statusChangeId || selectedRequest?.status_change_id) ? [selectedRequest] : []);

    if (!targetRows.length) return false;

    try {
      const failures = [];
      let lastErrorMessage = '';

      for (const row of targetRows) {
        const rowStatusChangeId = row?.statusChangeId ?? row?.status_change_id;
        const response = await UseAxios(createAuthorizedRequest(approveStatusChangeRequest(rowStatusChangeId, {
          is_approved: decision === 'approved' ? 1 : 2,
          approved_remarks: remarks || '',
          logged_in_emp_id: employeeId,
          approved_by: requestActorId,
          userInfo: mutationUserInfo,
        })));
        const data = getApiResponseData(response);
        if (data?.name === 'AxiosError' || data?.isAxiosError) {
          const apiMsg = data?.response?.data?.message || data?.response?.data?.details;
          if (apiMsg && typeof apiMsg === 'string') {
            lastErrorMessage = apiMsg;
          }
          failures.push(row?.scrn || row?.srNumber || String(rowStatusChangeId || ''));
        }
      }

      if (failures.length === targetRows.length) {
        throw new Error(
          lastErrorMessage
          || (decision === 'approved'
            ? 'Failed to approve selected requests.'
            : 'Failed to reject selected requests.'),
        );
      }

      if (failures.length > 0) {
        const processedCount = targetRows.length - failures.length;
        const decisionLabel = decision === 'approved' ? 'Approved' : 'Rejected';
        snackbarOpen(
          `${decisionLabel} ${processedCount} request(s). Failed: ${failures.join(', ')}`,
          'warning',
        );
      } else {
        snackbarOpen(
          decision === 'approved'
            ? (targetRows.length > 1 ? 'Selected requests approved successfully.' : 'Request approved successfully.')
            : (targetRows.length > 1 ? 'Selected requests rejected successfully.' : 'Request rejected successfully.'),
          'success',
        );
      }

      setStatusOpen(false);
      setBulkStatusRows([]);
      refreshTable();
      return true;
    } catch (error) {
      snackbarOpen(getApiErrorMessage(error, 'Failed to update request status.'), 'error');
      return false;
    }
  }, [
    bulkStatusRows,
    createAuthorizedRequest,
    disableUpdateStatusActions,
    employeeId,
    mutationUserInfo,
    refreshTable,
    requestActorId,
    selectedRequest,
    snackbarOpen,
  ]);

  const handleFilteredApiResponse = useCallback((response) => {
    const shouldHandleAsReset = (response?.isReset || pendingResetLikeResponseRef.current) && !statusChangeId;
    if (shouldHandleAsReset) {
      pendingResetLikeResponseRef.current = false;
      const targetUrl = originalProjectId
        ? buildRequestHistoryUrl({ projectId: originalProjectId })
        : buildRequestHistoryUrl({ mode: 'all' });

      if (!isResetRestoring) {
        startResetRestore(targetUrl, initialResetPayloadData);
      }

      if (currentHistoryUrl !== targetUrl && pendingHistoryUrlRef.current !== targetUrl) {
        pendingHistoryUrlRef.current = targetUrl;
        navigate(targetUrl, {
          replace: true,
          state: preservedRouteState,
        });
      }

      response.message = '0 results found.';
      response.meta = {
        ...(response.meta || {}),
        total: 0,
        page: 1,
        limit: paginationConfig.limit,
      };
      response.data = {
        ...(response.data || {}),
        tableData: [],
        meta: {
          ...(response.data?.meta || {}),
          total: 0,
          page: 1,
          limit: paginationConfig.limit,
        },
        data: {
          ...(response.data?.data || {}),
          tableData: [],
          meta: {
            ...(response.data?.data?.meta || {}),
            total: 0,
            page: 1,
            limit: paginationConfig.limit,
          },
        },
      };
      response.tableData = [];
      return response;
    }

    const responseRows = response?.data?.tableData
      ?? response?.data?.data?.tableData
      ?? response?.tableData
      ?? [];
    const responseConfiguration = response?.data?.tableConfiguration
      ?? response?.data?.data?.tableConfiguration
      ?? response?.tableConfiguration
      ?? response?.tableConfig
      ?? [];
    const transientCancelRefresh = transientCancelRefreshRef.current;
    let rawRows = Array.isArray(responseRows) ? responseRows : [];
    let rawConfiguration = Array.isArray(responseConfiguration) ? responseConfiguration : [];

    if (transientCancelRefresh?.statusChangeId && lastTableSnapshotRef.current) {
      const snapshotRows = Array.isArray(lastTableSnapshotRef.current?.rows)
        ? lastTableSnapshotRef.current.rows
        : [];
      const snapshotConfiguration = Array.isArray(lastTableSnapshotRef.current?.configuration)
        ? lastTableSnapshotRef.current.configuration
        : [];

      rawRows = snapshotRows.map((row) => {
        const rowId = Number(row?.statusChangeId ?? row?.status_change_id);
        if (rowId !== Number(transientCancelRefresh.statusChangeId)) {
          return row;
        }

        return {
          ...row,
          isApproved: 3,
          is_approved: 3,
          requestStatusValue: 3,
          request_status_value: 3,
          requestStatus: 'Cancelled',
          request_status: 'Cancelled',
          approvedRemarks: row?.approvedRemarks || row?.approved_remarks || 'Cancelled by user.',
          approved_remarks: row?.approved_remarks || row?.approvedRemarks || 'Cancelled by user.',
          statusDisplay: {
            ...(row?.statusDisplay || {}),
            label: 'Cancelled',
            value: 3,
            tone: 'canceled',
            isActionable: false,
            approvedRemarks: row?.statusDisplay?.approvedRemarks
              || row?.approvedRemarks
              || row?.approved_remarks
              || 'Cancelled by user.',
          },
        };
      });
      rawConfiguration = snapshotConfiguration;
    } else if (rawRows.length) {
      lastTableSnapshotRef.current = {
        rows: rawRows,
        configuration: rawConfiguration,
      };
    }

    const nextVisibleRows = rawRows;
    const nextVisibleRowIds = nextVisibleRows
      .filter((row) => isRequestActionable(row))
      .map((row) => getRowSelectionId(row))
      .filter(Boolean);
    const rawProjectNameFilter = getFilterItem(rawConfiguration, 'projectName');
    const rawProjectVerticalFilter = getFilterItem(rawConfiguration, 'projectVertical');
    const rawProjectTypeFilter = getFilterItem(rawConfiguration, 'projectType');
    const rawRequestStatusFilter = getFilterItem(
      rawConfiguration,
      [REQUEST_STATUS_FILTER_FIELD, 'requestStatus'],
    );
    const rawRequestedDateFilter = getFilterItem(rawConfiguration, 'requestedDate');
    const rawRequestedByFilter = getFilterItem(rawConfiguration, 'requestedBy');
    const rawApprovedByFilter = getFilterItem(rawConfiguration, 'approvedBy');
    const rawAssignedToMeFilter = getFilterItem(
      rawConfiguration,
      [ASSIGNED_TO_ME_FILTER_FIELD, 'assignedToMe'],
    );
    const requestedDateRangeCorrection = getRequestedDateRangeCorrection(
      rawRequestedDateFilter?.range,
    );
    if (requestedDateRangeCorrection) {
      rawConfiguration = applyRequestedDateRangeCorrection(
        rawConfiguration,
        requestedDateRangeCorrection.correctedRange,
      );
      const requestedDateRangeWarningMessage = [
        `Please select a Requested Date range within ${MAX_REQUESTED_DATE_RANGE_DAYS} days.`,
        `End date has been adjusted to ${requestedDateRangeCorrection.correctedEndDate}.`,
      ].join(' ');
      snackbarOpen(
        requestedDateRangeWarningMessage,
        'warning',
      );
    }
    const hasRawProjectNameSelection = normalizeFilterValues(rawProjectNameFilter?.selectedValues).length > 0;
    const hasRawProjectVerticalSelection = normalizeFilterValues(
      rawProjectVerticalFilter?.selectedValues,
    ).length > 0;
    const hasRawProjectTypeSelection = normalizeFilterValues(
      rawProjectTypeFilter?.selectedValues,
    ).length > 0;
    const hasRawRequestStatusSelection = normalizeFilterValues(rawRequestStatusFilter?.selectedValues).length > 0;
    const hasRawRequestedBySelection = normalizeFilterValues(
      rawRequestedByFilter?.selectedValues,
    ).length > 0;
    const hasRawApprovedBySelection = normalizeFilterValues(
      rawApprovedByFilter?.selectedValues,
    ).length > 0;
    const hasRawAssignedToMeSelection = normalizeFilterValues(
      rawAssignedToMeFilter?.selectedValues,
    ).length > 0;
    const hasRawRequestedDateSelection = Array.isArray(rawRequestedDateFilter?.range)
      && rawRequestedDateFilter.range[0]
      && rawRequestedDateFilter.range[1];
    const payloadProjectNameValues = normalizeFilterValues(tableRequestPayload?.project_name)
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    const payloadRequestStatusValues = normalizeFilterValues(tableRequestPayload?.request_status);
    const hasPayloadAssignedToMeFlag = Object.prototype.hasOwnProperty.call(
      tableRequestPayload || {},
      'assigned_to_me',
    );
    const hasPayloadAssignedToMeSelection = Boolean(tableRequestPayload?.assigned_to_me);
    const payloadRequestedDateRange = [
      tableRequestPayload?.requested_date_from || null,
      tableRequestPayload?.requested_date_to || null,
    ];
    const hasPayloadRequestedDateSelection = Boolean(
      payloadRequestedDateRange[0] && payloadRequestedDateRange[1],
    );
    const hasAnyRawFilterSelection = (
      hasRawProjectNameSelection
      || hasRawProjectVerticalSelection
      || hasRawProjectTypeSelection
      || hasRawRequestStatusSelection
      || hasRawRequestedBySelection
      || hasRawApprovedBySelection
      || hasRawAssignedToMeSelection
      || hasRawRequestedDateSelection
    );
    const hasAnyPayloadSeedSelection = (
      payloadProjectNameValues.length > 0
      || payloadRequestStatusValues.length > 0
      || hasPayloadAssignedToMeSelection
      || hasPayloadRequestedDateSelection
    );
    const nextApprovedByOptions = buildApprovedByFilterOptions(nextVisibleRows);
    const selectedApprovedByValues = normalizeFilterValues(
      getFilterItem(rawConfiguration, 'approvedBy')?.selectedValues,
    )
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    const approvedByOptionsWithSelections = [
      ...(nextApprovedByOptions.length ? nextApprovedByOptions : approvedByFilterOptionsRef.current),
      ...selectedApprovedByValues
        .filter((value) => !(nextApprovedByOptions.length ? nextApprovedByOptions : approvedByFilterOptionsRef.current)
          .some((option) => String(option?.value || '').trim() === value))
        .map((value) => ({ value, label: value })),
    ];
    approvedByFilterOptionsRef.current = approvedByOptionsWithSelections;
    const shouldSeedInitialProjectName = (
      !suppressDefaultSeedStateRef.current
      && !isAllProjectsMode
      && !hasConsumedInitialProjectSeedRef.current
      && !hasRawProjectNameSelection
      && Boolean(projectName)
    );
    const shouldSeedInitialRequestStatus = (
      !suppressDefaultSeedStateRef.current
      && !hasConsumedReqStatusSeedRef.current
      && !hasRawRequestStatusSelection
      && !statusChangeId
    );
    const shouldSeedInitialAssignedToMe = (
      !suppressDefaultSeedStateRef.current
      && !hasConsumedAssignedToMeSeedRef.current
      && !hasRawAssignedToMeSelection
      && !statusChangeId
      && !isFromNotification
      && Boolean(initialResetPayloadData?.assigned_to_me)
    );
    const shouldSeedInitialRequestedDate = (
      !suppressDefaultSeedStateRef.current
      && !hasConsumedRequestedDateSeedRef.current
      && !hasRawRequestedDateSelection
      && !statusChangeId
    );
    const seedsPending = (
      !hasConsumedReqStatusSeedRef.current
      || !hasConsumedAssignedToMeSeedRef.current
      || !hasConsumedRequestedDateSeedRef.current
    );
    const shouldHideAllProjectsRows = (
      isAllProjectsMode
      && !statusChangeId
      && !hasAnyRawFilterSelection
      && !hasAnyPayloadSeedSelection
      && !seedsPending
    );
    const responseProjectSeedValues = hasRawProjectNameSelection
      ? []
      : (shouldSeedInitialProjectName ? [projectName] : payloadProjectNameValues);
    const responseRequestStatusSeedValues = hasRawRequestStatusSelection
      ? []
      : (shouldSeedInitialRequestStatus ? [0] : payloadRequestStatusValues);
    const responseAssignedToMeSeedValues = hasRawAssignedToMeSelection
      ? []
      : (shouldSeedInitialAssignedToMe || hasPayloadAssignedToMeSelection ? ['Assigned to me'] : []);
    const responseRequestedDateSeedRange = hasRawRequestedDateSelection
      ? []
      : (
        shouldSeedInitialRequestedDate
          ? getDefaultRequestedDateRange()
          : (hasPayloadRequestedDateSelection ? payloadRequestedDateRange : [])
      );
    const initialProjectSeedValues = normalizeFilterValues(initialResetPayloadData?.project_name)
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    const initialRequestStatusSeedValues = normalizeFilterValues(initialResetPayloadData?.request_status);
    const initialAssignedToMeSeedValues = initialResetPayloadData?.assigned_to_me ? ['Assigned to me'] : [];
    const initialRequestedDateSeedRange = (
      initialResetPayloadData?.requested_date_from && initialResetPayloadData?.requested_date_to
        ? [
          initialResetPayloadData.requested_date_from,
          initialResetPayloadData.requested_date_to,
        ]
        : []
    );
    const effectiveProjectSeedValues = forceDefaultUiSeedsRef.current
      ? initialProjectSeedValues
      : responseProjectSeedValues;
    const effectiveRequestStatusSeedValues = forceDefaultUiSeedsRef.current
      ? initialRequestStatusSeedValues
      : responseRequestStatusSeedValues;
    const effectiveAssignedToMeSeedValues = forceDefaultUiSeedsRef.current
      ? initialAssignedToMeSeedValues
      : responseAssignedToMeSeedValues;
    const effectiveRequestedDateSeedRange = forceDefaultUiSeedsRef.current
      ? initialRequestedDateSeedRange
      : responseRequestedDateSeedRange;
    const effectiveVisibleRows = shouldHideAllProjectsRows ? [] : nextVisibleRows;
    const effectiveVisibleRowIds = shouldHideAllProjectsRows ? [] : nextVisibleRowIds;

    setVisibleRows(effectiveVisibleRows);
    setVisibleRowIds(effectiveVisibleRowIds);
    setSelectedRowIds((current) => current.filter((rowId) => effectiveVisibleRowIds.includes(rowId)));

    response.data = {
      ...(response.data || {}),
      tableConfiguration: rawConfiguration,
      tableData: effectiveVisibleRows,
      data: {
        ...(response.data?.data || {}),
        tableConfiguration: rawConfiguration,
        tableData: effectiveVisibleRows,
      },
    };
    response.tableConfiguration = rawConfiguration;
    response.tableConfig = rawConfiguration;
    response.tableData = effectiveVisibleRows;

    if (shouldHideAllProjectsRows) {
      response.message = '0 results found.';
      response.meta = {
        ...(response.meta || {}),
        total: 0,
        page: 1,
        limit: paginationConfig.limit,
      };
      response.data = {
        ...(response.data || {}),
        tableData: [],
        meta: {
          ...(response.data?.meta || {}),
          total: 0,
          page: 1,
          limit: paginationConfig.limit,
        },
        data: {
          ...(response.data?.data || {}),
          tableData: [],
          meta: {
            ...(response.data?.data?.meta || {}),
            total: 0,
            page: 1,
            limit: paginationConfig.limit,
          },
        },
      };
      response.tableData = effectiveVisibleRows;
      setSelectedRowIds([]);
    }

    const normalizedResponse = normalizeRequestHistoryResponse(response, {
      filterRequestScope: {
        cacheScope: employeeId ? String(employeeId).trim() : '',
        loggedInEmpId: employeeId ? String(employeeId).trim() : '',
        projectId: selectedProjectContext?.projectId
          ?? tableRequestPayload?.project_id
          ?? projectId
          ?? '',
        projectSeqId: selectedProjectContext?.projectSeqId
          ?? tableRequestPayload?.project_seq_id
          ?? projectSeqId
          ?? '',
        projectName: normalizeFilterValues(
          tableRequestPayload?.project_name
          ?? additionalPayloadData?.project_name
          ?? (projectName ? [projectName] : []),
        ),
      },
      projectFilterSeedValues: effectiveProjectSeedValues,
      baseProjectNameValues: projectName ? [projectName] : [],
      requestStatusSeedValues: effectiveRequestStatusSeedValues,
      assignedToMeSeedValues: effectiveAssignedToMeSeedValues,
      assignedToMeExplicitlyCleared: hasPayloadAssignedToMeFlag && !hasPayloadAssignedToMeSelection,
      requestedDateSeedRange: effectiveRequestedDateSeedRange,
      forceDefaultProjectName: forceDefaultUiSeedsRef.current,
      forceDefaultRequestStatus: forceDefaultUiSeedsRef.current && !statusChangeId,
      forceDefaultAssignedToMe: forceDefaultUiSeedsRef.current && !statusChangeId,
      forceDefaultRequestedDate: forceDefaultUiSeedsRef.current && !statusChangeId,
      approvedByOptions: approvedByFilterOptionsRef.current,
      rowIds: effectiveVisibleRowIds,
      selectedRowIds,
      canUpdate,
      canDelete,
      canShareLink,
      canViewStatus: canReadStatusColumn,
      canUpdateStatus,
      onDeleteClick: handleDeleteRequest,
      onEditClick: openEditRequest,
      onShareClick: handleCopyShareLink,
      onScrnClick: openViewRequest,
      onStatusClick: openStatusRequest,
      onToggleRowSelect: handleToggleRowSelect,
      onToggleSelectAll: handleToggleSelectAll,
    });
    const normalizedConfiguration = normalizedResponse?.data?.tableConfiguration
      ?? normalizedResponse?.data?.data?.tableConfiguration
      ?? normalizedResponse?.tableConfiguration
      ?? normalizedResponse?.tableConfig
      ?? [];
    if (hasRawProjectNameSelection || shouldSeedInitialProjectName) {
      hasConsumedInitialProjectSeedRef.current = true;
    }
    if (hasRawRequestStatusSelection || shouldSeedInitialRequestStatus) {
      hasConsumedReqStatusSeedRef.current = true;
    }
    if (hasRawAssignedToMeSelection || shouldSeedInitialAssignedToMe || !initialResetPayloadData?.assigned_to_me) {
      hasConsumedAssignedToMeSeedRef.current = true;
    }
    if (hasRawRequestedDateSelection || shouldSeedInitialRequestedDate) {
      hasConsumedRequestedDateSeedRef.current = true;
    }
    if (hasAnyRawFilterSelection) {
      suppressDefaultSeedStateRef.current = false;
    }

    const hasEffectiveProjectSelection = (
      hasRawProjectNameSelection
      || payloadProjectNameValues.length > 0
    );
    const shouldPreserveProjectScopedPayload = (
      !isAllProjectsMode
      && !hasEffectiveProjectSelection
      && Boolean(
        originalProjectId
        || originalProjectSeqId
        || projectId
        || projectSeqId
        || projectName,
      )
    );
    const shouldPreserveBaseProjectScope = (
      shouldPreserveProjectScopedPayload
      || (
        !(
          previousProjectFilterSelectedRef.current
          && !hasRawProjectNameSelection
        )
        && (
          !hasConsumedInitialProjectSeedRef.current
          && Boolean(
            baseAdditionalPayloadData?.project_name
            || baseAdditionalPayloadData?.project_id
            || baseAdditionalPayloadData?.project_seq_id,
          )
        )
      )
    );
    const nextAdditionalPayload = getAppliedAdditionalPayload(
      normalizedConfiguration,
      baseAdditionalPayloadData,
      {
        preserveBaseProjectScope: shouldPreserveBaseProjectScope,
      },
    );
    const userClearedProjectSelection = (
      !forceDefaultUiSeedsRef.current
      && previousProjectFilterSelectedRef.current
      && !hasRawProjectNameSelection
    );
    if (hasRawProjectNameSelection || userClearedProjectSelection) {
      delete nextAdditionalPayload.project_id;
      delete nextAdditionalPayload.project_seq_id;
    }
    if (!userClearedProjectSelection && !hasRawProjectNameSelection && payloadProjectNameValues.length) {
      nextAdditionalPayload.project_name = payloadProjectNameValues;
      const hasSinglePayloadProjectMatch = (
        payloadProjectNameValues.length === 1
        && String(payloadProjectNameValues[0] || '')
          .trim()
          .toLowerCase()
        === String(projectName || '')
          .trim()
          .toLowerCase()
      );

      if (!hasSinglePayloadProjectMatch && !shouldPreserveBaseProjectScope && !baseAdditionalPayloadData?.project_id) {
        delete nextAdditionalPayload.project_id;
        delete nextAdditionalPayload.project_seq_id;
      }
    }
    if (!hasRawProjectVerticalSelection) {
      const payloadProjectVerticalValues = normalizeFilterValues(tableRequestPayload?.project_vertical);
      if (payloadProjectVerticalValues.length) {
        nextAdditionalPayload.project_vertical = payloadProjectVerticalValues;
      }
    }
    if (!hasRawProjectTypeSelection) {
      const payloadProjectTypeValues = normalizeFilterValues(tableRequestPayload?.project_type);
      if (payloadProjectTypeValues.length) {
        nextAdditionalPayload.project_type = payloadProjectTypeValues;
      }
    }
    if (!hasRawRequestedBySelection) {
      const payloadRequestedByValues = normalizeFilterValues(tableRequestPayload?.requested_by);
      if (payloadRequestedByValues.length) {
        nextAdditionalPayload.requested_by = payloadRequestedByValues;
      }
    }
    if (!hasRawApprovedBySelection) {
      delete nextAdditionalPayload.approved_by;
    }
    if (!hasRawRequestStatusSelection && payloadRequestStatusValues.length) {
      nextAdditionalPayload.request_status = payloadRequestStatusValues;
    }
    if (!hasRawAssignedToMeSelection) {
      nextAdditionalPayload.assigned_to_me = false;
    }
    if (!hasRawRequestedDateSelection && hasPayloadRequestedDateSelection) {
      nextAdditionalPayload.requested_date_from = payloadRequestedDateRange[0];
      nextAdditionalPayload.requested_date_to = payloadRequestedDateRange[1];
    }
    if (shouldSeedInitialProjectName && projectName) {
      nextAdditionalPayload.project_name = [projectName];
    }
    if (shouldSeedInitialRequestStatus) {
      nextAdditionalPayload.request_status = [0];
    }
    if (shouldSeedInitialAssignedToMe) {
      nextAdditionalPayload.assigned_to_me = true;
    }
    const appliedProjectNameValues = normalizeFilterValues(
      nextAdditionalPayload?.project_name,
    )
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    const hasSingleAppliedProject = appliedProjectNameValues.length === 1;
    const normalizedAppliedProjectName = hasSingleAppliedProject
      ? appliedProjectNameValues[0].toLowerCase()
      : '';
    const matchingAppliedProjectRows = hasSingleAppliedProject
      ? nextVisibleRows.filter((row) => (
        String(row?.projectName ?? row?.project_name ?? '')
          .trim()
          .toLowerCase() === normalizedAppliedProjectName
      ))
      : [];
    const uniqueAppliedProjectIds = matchingAppliedProjectRows.reduce((acc, row) => {
      const rowProjectId = row?.projectId ?? row?.project_id;
      if (!rowProjectId) {
        return acc;
      }

      const normalizedRowProjectId = String(rowProjectId).trim();
      return acc.includes(normalizedRowProjectId)
        ? acc
        : [...acc, normalizedRowProjectId];
    }, []);
    const normalizedOriginalProjectName = String(
      routeProjectName
      || projectData?.project_name
      || projectData?.projectName
      || '',
    )
      .trim()
      .toLowerCase();
    const projectNameOptionItem = getFilterItem(rawConfiguration, 'projectName');
    const projectNameOptionList = Array.isArray(projectNameOptionItem?.options) ? projectNameOptionItem.options : [];
    const matchedProjectOption = hasSingleAppliedProject
      ? projectNameOptionList.find((opt) => (
        String(opt?.label || opt?.value || '')
          .trim()
          .toLowerCase() === normalizedAppliedProjectName
      ))
      : null;
    const optionProjectId = matchedProjectOption?.id
      ?? matchedProjectOption?.projectId
      ?? matchedProjectOption?.project_id
      ?? null;

    const resolvedAppliedProjectId = uniqueAppliedProjectIds.length === 1
      ? uniqueAppliedProjectIds[0]
      : (
        hasSingleAppliedProject
          ? (
            optionProjectId
              ? String(optionProjectId)
              : (
                normalizedOriginalProjectName === normalizedAppliedProjectName && originalProjectId
                  ? String(originalProjectId)
                  : ''
              )
          )
          : ''
      );
    const currentScopedProjectId = String(
      queryProjectId
      || projectId
      || originalProjectId
      || '',
    ).trim();
    const currentScopeKey = isAllProjectsMode
      ? 'all'
      : (currentScopedProjectId ? `project:${currentScopedProjectId}` : 'project');
    const desiredScopeKey = hasSingleAppliedProject
      ? (
        resolvedAppliedProjectId
          ? `project:${String(resolvedAppliedProjectId).trim()}`
          : null
      )
      : 'all';

    setAdditionalPayloadData((currentPayload) => (
      arePayloadsEqual(currentPayload, nextAdditionalPayload)
        ? currentPayload
        : nextAdditionalPayload
    ));
    const nextAllProjectsUrl = buildRequestHistoryUrl({ mode: 'all' });
    if (!projectDetailsLoading && !statusChangeId && desiredScopeKey && desiredScopeKey !== currentScopeKey) {
      if (hasSingleAppliedProject && resolvedAppliedProjectId) {
        const nextUrl = buildRequestHistoryUrl({ projectId: resolvedAppliedProjectId });
        if (currentHistoryUrl !== nextUrl && pendingHistoryUrlRef.current !== nextUrl) {
          pendingHistoryUrlRef.current = nextUrl;
          navigate(nextUrl, {
            replace: true,
            state: {
              ...preservedRouteState,
              projectId: String(resolvedAppliedProjectId),
              projectName: appliedProjectNameValues[0],
            },
          });
        }
      } else if (currentHistoryUrl !== nextAllProjectsUrl && pendingHistoryUrlRef.current !== nextAllProjectsUrl) {
        pendingHistoryUrlRef.current = nextAllProjectsUrl;
        navigate(nextAllProjectsUrl, {
          replace: true,
          state: {},
        });
      }
    }
    transientCancelRefreshRef.current = null;
    if (nextVisibleRows.length) {
      lastTableSnapshotRef.current = {
        rows: nextVisibleRows,
        configuration: rawConfiguration,
      };
    }
    forceDefaultUiSeedsRef.current = false;
    return normalizedResponse;
  }, [
    tableRequestPayload,
    baseAdditionalPayloadData,
    employeeId,
    handleDeleteRequest,
    handleToggleRowSelect,
    handleToggleSelectAll,
    openEditRequest,
    openViewRequest,
    openStatusRequest,
    isAllProjectsMode,
    navigate,
    location.search,
    currentHistoryUrl,
    tableRequestPayload,
    isResetRestoring,
    preservedRouteState,
    projectData,
    projectDetailsLoading,
    projectId,
    projectName,
    projectSeqId,
    queryProjectId,
    originalProjectId,
    originalProjectSeqId,
    routeProjectName,
    statusChangeId,
    canReadStatusColumn,
    canDelete,
    canUpdate,
    canUpdateStatus,
    initialResetPayloadData,
    selectedRowIds,
    snackbarOpen,
    startResetRestore,
  ]);

  const handleAddModalClose = useCallback(() => {
    setAddOpen(false);
    setAddBlocked(false);
    setAddBlockedMessage('');
  }, []);

  const paginationConfig = useMemo(() => ({
    page: 1,
    limit: 10,
    rowsPerPageOptions: [10, 20, 30, 40],
  }), []);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      stateKey: requestHistoryTableStateKey,
      stateStorage: 'session',
      userInfo: {
        ...mutationUserInfo,
        globalColors: resolvedProfile?.siteConfiguration?.global_colors,
      },
      apiEndpoints: {
        getFilteredData: { endpoint: '', enabled: true },
        download: { endpoint: 'export', enabled: false },
      },
      additionalPayloadData: tableRequestPayload,
      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse,
      },
      headers: {
        token: accessToken,
      },
      showToolBar: true,
      showPagination: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showFilterOption: true,
        showToolBarHeading: true,
        toolBarHeading: 'Status change requests',
        showSaveView: false,
        showReset: true,
        onResetClick: handleResetToInitialState,
        showExpandCollapse: false,
        additionalElements: canCreate ? [
          <Tooltip
            key="new-request"
            title={!hasSingleProjectNameSelected ? 'Select a single Project Name' : 'Add new status'}
            arrow
          >
            <span className="request-history-add-btn-shell">
              <button
                type="button"
                className="request-history-add-btn"
                onClick={openAddRequestModal}
                disabled={!hasSingleProjectNameSelected || checkingAddAvailability}
              >
                <span className="request-history-add-btn__icon" aria-hidden="true" />
              </button>
            </span>
          </Tooltip>,
        ] : [],
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 10,
      stateKey: requestHistoryTableStateKey,
      stateStorage: 'session',
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 5,
      },
      paginationConfig,
      showExpandCollapse: false,
      tableScrollHeight,
      maxFrozenColumns: {
        desktop: 2,
        tablet: 2,
        mobile: 1,
      },
    },
  }), [
    accessToken,
    canCreate,
    checkingAddAvailability,
    handleFilteredApiResponse,
    handleResetToInitialState,
    isProjectNameSelected,
    mutationUserInfo,
    openAddRequestModal,
    paginationConfig,
    requestHistoryStorageKey,
    requestHistoryTableStateKey,
    resolvedProfile,
    tableScrollHeight,
    tableRequestPayload,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const availableTableHeight = Math.min(
        Math.max(window.innerHeight - 360, 260),
        420,
      );
      setTableScrollHeight(`${availableTableHeight}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTableReady = Boolean(
    accessToken
    && (projectId || isAllProjectsMode),
  );
  const shouldRenderTable = isTableReady && !isResetRestoring;
  const tableKey = useMemo(
    () => [
      'request-history',
      employeeId,
      requestHistoryMode || 'project',
      projectId,
      projectSeqId,
      statusChangeId,
      tableRefreshNonce,
    ]
      .filter(Boolean)
      .join('-'),
    [employeeId, projectId, projectSeqId, requestHistoryMode, statusChangeId, tableRefreshNonce],
  );

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="request-history-page">
      <div className="request-history-page-header">
        <h2>Status History</h2>
      </div>

      <div className="request-history-main">
        <div className="request-history-selection-panel">
          <div className="request-history-selection-panel__summary">
            <div>
              Number of staffing request selected : {selectedRows.length}
            </div>
            <div>
              Approval Status :{' '}
              <button
                type="button"
                className="request-history-selection-panel__open-link"
                onClick={handleOpenSelected}
                disabled={!canUpdateStatus || !actionableSelectedRows.length}
              >
                Open
              </button>
            </div>
          </div>
        </div>
        <div
          className={[
            'table-utility',
            'request-history-table-utility',
            canShowActionColumn ? 'request-history-table-utility--with-action' : '',
            hasSingleActionColumn ? 'request-history-table-utility--single-action' : '',
          ].filter(Boolean).join(' ')}
        >
          {shouldRenderTable ? (
            <RequestHistorySelectionContext.Provider
              value={{
                rowIds: visibleRowIds,
                selectedRowIds,
                canUpdateStatus,
                onToggleRowSelect: handleToggleRowSelect,
                onToggleSelectAll: handleToggleSelectAll,
              }}
            >
              <TableUtility key={tableKey} configuration={configuration} pageSize={10} />
            </RequestHistorySelectionContext.Provider>
          ) : (
            <div className="request-history-loading">Loading...</div>
          )}
        </div>
        <div className="modal-actions request-history-footer">
          <button type="button" className="btn-cancel" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
      <ViewRequestModal
        isOpen={viewOpen}
        request={mapRowForModal(selectedRequest)}
        onClose={() => setViewOpen(false)}
      />
      <AddRequestModal
        isOpen={addOpen}
        onClose={handleAddModalClose}
        onSubmit={handleAddSubmit}
        projectName={selectedProjectNameLabel}
        currentStatus={selectedProjectCurrentStatus}
        isBlocked={addBlocked}
        isSubmitDisabled={disableCreateActions}
        blockedMessage={addBlockedMessage}
      />
      <EditRequestModal
        isOpen={editOpen}
        request={mapRowForModal(selectedRequest)}
        onClose={() => {
          setEditOpen(false);
          setEditRequestLoading(false);
        }}
        onSubmit={handleEditSubmit}
        isSubmitDisabled={disableUpdateActions || editRequestLoading}
        isRequestLoading={editRequestLoading}
      />
      <RequestStatusModal
        isOpen={statusOpen}
        request={bulkStatusRows.length ? selectedRequest : mapRowForModal(selectedRequest)}
        onClose={() => {
          setStatusOpen(false);
          setBulkStatusRows([]);
        }}
        onSubmit={handleStatusSubmit}
        isSubmitDisabled={disableUpdateStatusActions}
      />
      <CancelRequestModal
        isOpen={cancelOpen}
        requestNumber={selectedRequest?.scrn || selectedRequest?.srNumber || ''}
        isSubmitting={cancelSubmitting}
        isConfirmDisabled={disableDeleteActions}
        onClose={() => setCancelOpen(false)}
        onConfirm={confirmCancelRequest}
      />
    </div>
  );
};

export default RequestHistoryPage;
